/** Thin API client. Cookies carry the session; nothing here holds credentials. */
import type { Currency, PublicPlan } from '@krevya/shared';
import { translateNow } from './i18n/index.js';
import type {
  EmailDraft,
  EmailFlow,
  EmailFlowSettings,
  EmailFlowsOverview,
  EmailLanguage,
  EmailPreview,
  EmailStepTemplate,
  EmailTemplateContent,
  EmailTestSendResult,
  SendingDomain,
} from './marketing/email-types.js';

export type Workspace = { workspaceId: string; name: string; role: string };

/** A store the merchant can install into, read from Shopify after sign-in. */
export type ShopifyStoreChoice = {
  /** Canonical myshopify address; what an install actually needs. */
  shopName: string;
  /** What the merchant calls the store. */
  name: string;
  /** The merchant's own domain, when they have one. Arrives as a full URL. */
  primaryDomain: string | null;
  url: string | null;
  shopifyShopId: string | null;
  storeType: string | null;
  createdAt: string | null;
  /** The app has to be created here; a store from another one cannot install. */
  organizationId: string;
  organizationName: string;
};

/**
 * Which products are worth the expensive work, and on what evidence.
 *
 * `source` matters as much as the list: real orders are a measurement,
 * Shopify's best-selling order is an ordinal with no magnitude behind it, and
 * catalogue position is a proxy for what the merchant merchandised first. A
 * screen that presented all three identically would be claiming to know
 * something it does not.
 */
export type SuggestedProducts = {
  source: 'orders' | 'storefront' | 'catalog' | 'none';
  windowDays: number | null;
  products: {
    id: string;
    externalId: string;
    title: string;
    imageUrl: string | null;
    unitsSold: number | null;
    revenueMicro: number | null;
  }[];
  /** What kind of shop this is, so the studio can say what it is writing for. */
  store: {
    category: string;
    priceBand: 'budget' | 'mid' | 'premium' | 'luxury' | null;
    audiences: string[];
    source: 'inferred' | 'confirmed' | 'edited';
  } | null;
};

/** One kind of buyer, as Krevya read them out of the catalogue. */
export type StoreAudience = {
  name: string;
  description: string;
  motivations: string[];
  channels: string[];
};

/**
 * What the store sells and who buys it.
 *
 * `source` is not decoration. `inferred` means nobody has looked at this yet
 * and the screen has to say so; `confirmed` means a person read it and agreed;
 * `edited` means they corrected it. Presenting the first as though it were the
 * third is how a merchant stops trusting the whole screen.
 */
export type StoreProfile = {
  category: string;
  subcategory: string | null;
  priceLowMicro: number | null;
  priceHighMicro: number | null;
  currency: string | null;
  priceBand: 'budget' | 'mid' | 'premium' | 'luxury' | null;
  audiences: StoreAudience[];
  productSampleSize: number;
  source: 'inferred' | 'confirmed' | 'edited';
  confidence: 'low' | 'medium' | 'high';
  evidence: {
    productTypes: { value: string; count: number }[];
    tags: { value: string; count: number }[];
  };
  updatedAt: number;
};

export type Integration = {
  id: string;
  provider: string;
  storeDomain: string;
  displayName: string | null;
  status: 'active' | 'disconnected' | 'invalid_token';
  /** Permissions Krevya now asks for that this store has not granted yet. */
  missingScopes?: string[];
  lastSyncAt: number | null;
  lastSyncStatus: string | null;
  /** Products the store currently offers, as the last sync left them. */
  productCount: number;
  /** When the shop was first connected, epoch milliseconds. A reconnect of
      the same domain updates the row rather than replacing it, so this stays
      the day the merchant started. */
  createdAt: number;
};

/** How a storefront popup reaches Krevya. The key is public by design. */
export type StorefrontConnection = {
  storefrontKey: string;
  shop: string;
  signupEndpoint: string;
  missingScopes: string[];
};

/** One popup signup; its id is the operation id the storefront got back. */
export type StorefrontSignup = {
  id: string;
  email: string;
  source: string;
  tags: string[];
  status: 'pending' | 'synced' | 'failed';
  outcome: 'created' | 'updated' | null;
  attempts: number;
  lastError: string | null;
  shopifyCustomerId: string | null;
  pageUrl: string | null;
  createdAt: number;
  syncedAt: number | null;
};

export type StorefrontSignupSummary = {
  total: number;
  byStatus: Record<StorefrontSignup['status'], number>;
  bySource: Record<'wheel' | 'scratch' | 'email', number>;
  lastSevenDays: number;
  lastSignupAt: number | null;
};

export type StorefrontSignupFilter = {
  status?: StorefrontSignup['status'] | '' | undefined;
  source?: 'wheel' | 'scratch' | 'email' | '' | undefined;
  email?: string | undefined;
  before?: number | null | undefined;
};

export type ImportedBrand = {
  name: string | null;
  tone: string | null;
  audience: string | null;
  sells: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  logoUrl: string | null;
  bannedClaims: string[];
  defaultLanguage: string | null;
};

export type StoreImport = {
  importId: string;
  url: string;
  status: 'running' | 'complete' | 'partial' | 'failed';
  stage: 'reading_shop' | 'reading_brand' | 'done' | null;
  productCount: number;
  catalogueSource: string | null;
  brand: ImportedBrand | null;
  error: string | null;
  products: { id: string; title: string; imageUrl: string | null; price: string | null }[];
};

/**
 * Where the first-run setup stands: what exists, and what was decided here.
 *
 * Mirrors `OnboardingStateView` in packages/core/src/onboarding/progress.ts.
 * A Shopify catalogue is never `running` here: a sync records only how it
 * ended, and the screen that starts one knows it is running itself.
 */
export type OnboardingState = {
  store: {
    connected: boolean;
    id: string | null;
    provider: 'shopify' | 'url' | null;
    domain: string | null;
    name: string | null;
    status: 'active' | 'invalid_token' | null;
  };
  catalog: {
    status: 'none' | 'pending' | 'running' | 'partial' | 'completed' | 'failed';
    count: number;
    failedCount: number | null;
    source: 'shopify_sync' | 'url_import' | null;
    importId: string | null;
    lastSyncAt: number | null;
  };
  brand: { exists: boolean; kitId: string | null; decision: 'pending' | 'confirmed' | 'skipped' };
  steps: { storeCompletedAt: number | null; catalogCompletedAt: number | null };
  firstCreative: { startedAt: number | null };
  dismissedAt: number | null;
};

/** A brand read from the connected store. A proposal: nothing is saved until confirmed. */
export type StoreBrandProposal = {
  brand: (ImportedBrand & { palette: string[]; headingFont: string | null }) | null;
  sources: {
    name: 'shopify' | 'products' | null;
    tone: 'products' | null;
    colors: 'storefront' | null;
  } | null;
};

export type Product = {
  id: string;
  title: string;
  vendor: string | null;
  status: 'active' | 'archived' | 'draft' | 'unavailable';
  currency: string | null;
  minPrice: string | null;
  maxPrice: string | null;
  primaryImageUrl: string | null;
  imageCount: number;
  /** Krevya's own work on the product: the facts the home screen reads. */
  researched: boolean;
  pictures: number;
  storePageWritten: boolean;
  storePageRunning: boolean;
  draftInShopify: boolean;
  liveInShopify: boolean;
};

export type SyncResult = {
  status: 'success' | 'partial' | 'failed';
  productsUpserted: number;
  productsMarkedUnavailable: number;
  error?: string;
};

export type Project = {
  id: string;
  name: string;
  status: 'active' | 'archived';
  productId: string;
  productTitle: string;
  productStatus: Product['status'];
  productImageUrl: string | null;
  createdAt: number;
};

export type CreativeSet = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  versionId: string;
  versionNumber: number;
  /** Only choosable with research behind it; the server refuses it otherwise. */
  requiresEvidence: boolean;
};

export type ModelProfile = {
  id: string;
  slug: string;
  displayName: string;
  description: string | null;
  qualityTier: 'fast' | 'balanced' | 'high';
  provider: string;
  creditPerImage: number;
  capabilities: { ratios: string[]; maxReferenceImages: number };
};

export type CreativeOptions = {
  ratios: string[];
  languages: string[];
  quantity: { min: number; max: number };
  maxCustomDirectionLength: number;
};

export type Quote = {
  quantity: number;
  creditsPerImage: number;
  totalCredits: number;
  currency: 'credits';
  availableCredits: number;
  canAfford: boolean;
  creativeSet: { id: string; name: string; versionNumber: number };
  modelProfile: { id: string; name: string; slug: string };
  product: { id: string; title: string; status: string };
  project: { id: string; name: string };
};

/** A video character/product reference (docs/video-generation-plan.md §4, §11 task 9). */
export type VideoCharacterKind = 'character' | 'product';
export type VideoCharacterImageRole = 'frontal' | 'angle';

export type VideoCharacterImage = {
  id: string;
  assetId: string;
  role: VideoCharacterImageRole;
  position: number;
};

/** One provider's element/binding state for a character; one row per provider tried. */
export type VideoCharacterBinding = {
  provider: string;
  status: string;
  providerElementId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
};

export type VideoCharacter = {
  id: string;
  name: string;
  description: string;
  kind: VideoCharacterKind;
  status: string;
  brandKitId: string | null;
  createdAt: number;
  updatedAt: number;
  images: VideoCharacterImage[];
  bindings: VideoCharacterBinding[];
};

/** `GET /api/video-models`, active `media_type='video'` catalog rows. */
export type VideoModelCapabilities = { ratios: string[]; durationSecondsRange: [number, number] };

export type VideoModel = {
  id: string;
  slug: string;
  displayName: string;
  provider: string;
  providerModel: string;
  creditPerSecond: number | null;
  creditPerVideo: number | null;
  capabilities: VideoModelCapabilities;
  /** The most this profile is priced for: the composer offers no more. */
  maxResolution: '720p' | '1080p';
  supportsAudio: boolean;
  /** 'create' | 'motion_control' | 'edit' (docs/video-motion-edit-plan.md §1.1, §8). */
  videoMode: VideoMode;
};

export type VideoQuote = {
  quantity: number;
  durationSeconds: number;
  /** The price of one video, already resolved from whichever pricing field the model uses. */
  creditPerVideo: number;
  totalCredits: number;
  currency: 'credits';
  availableCredits: number;
  canAfford: boolean;
  ratio: string;
  language: string;
  creativeSet: { id: string; name: string; versionId: string; versionNumber: number };
  modelProfile: { id: string; name: string; slug: string; provider: string };
  product: { id: string; title: string; status: string };
  project: { id: string; name: string };
  videoMode: VideoMode;
  motionVideoAssetId: string | null;
  sourceVideoAssetId: string | null;
  characterOrientation: 'video' | 'image' | null;
};

/** 'create' | 'motion_control' | 'edit' (docs/video-motion-edit-plan.md §1.1). */
export type VideoMode = 'create' | 'motion_control' | 'edit';

export type VideoQuoteInput = {
  workspaceId: string;
  productProjectId: string;
  modelProfileId: string;
  ratio: string;
  durationSeconds: number;
  quantity: number;
  language: string;
  brandColor: string;
  customDirection?: string | null;
  withAudio?: boolean;
  /** '720p' | '1080p'. Not priced (only duration/quantity are). */
  resolution?: string | null;
  /** A saved video_characters row, or omitted for a characterless video. */
  videoCharacterId?: string | null;
  /**
   * Motion control / video edit (docs/video-motion-edit-plan.md §1.3, §8).
   * All optional and default to 'create', so existing callers are unaffected.
   */
  videoMode?: VideoMode | null;
  /** motion_control only: the uploaded motion-source video (`assets.type='uploaded_video'`). */
  motionVideoAssetId?: string | null;
  /** edit only: the uploaded video being edited. Its duration overrides `durationSeconds`. */
  sourceVideoAssetId?: string | null;
  /** motion_control only: 'video' (default, caps duration at 30s) | 'image' (caps at 10s). */
  characterOrientation?: 'video' | 'image' | null;
  /** edit only: additional "element" reference images, ≤30. */
  referenceImageAssetIds?: string[] | null;
};

/**
 * Errors carry the taxonomy code, so screens can react to a known state
 * instead of pattern-matching a message. Raw provider text never reaches here.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    /**
     * What actually went wrong, when the server was willing to say.
     *
     * Codes are for branching; this is for reading. "INTERNAL_ERROR" cannot
     * tell a merchant that their model provider is out of credit, and that is
     * the difference between them fixing it in a minute and filing a bug.
     */
    readonly detail?: string,
    /**
     * Structured facts that came with the error, such as how long to wait.
     * Only the admin gate reads them; every other screen branches on `code`.
     */
    readonly details?: { retryAfterSeconds?: number },
  ) {
    super(detail ?? code);
    this.name = 'ApiError';
  }
}

let adminAccessHandler: ((code: string) => void) | null = null;

/** The admin gate registers here to hear that the admin session is gone. */
export function onAdminAccessError(handler: ((code: string) => void) | null): void {
  adminAccessHandler = handler;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { code?: string; details?: { retryAfterSeconds?: unknown } };
    } | null;
    // The wait comes from the body or, failing that, the standard header; the
    // gate shows a countdown from it rather than letting someone hammer a lock.
    const fromBody = body?.error?.details?.retryAfterSeconds;
    const fromHeader = Number(response.headers.get('retry-after'));
    const retryAfterSeconds =
      typeof fromBody === 'number' ? fromBody : fromHeader > 0 ? fromHeader : undefined;
    const code = body?.error?.code ?? 'INTERNAL_ERROR';
    // An admin session that ended, or was never opened, is a fact about the
    // whole admin area and not about the call that found it out: the gate has
    // to swap to its verify screen whichever page was reading.
    if (code.startsWith('ADMIN_SESSION_') || code === 'ADMIN_MFA_ENROLLMENT_REQUIRED') {
      adminAccessHandler?.(code);
    }
    throw new ApiError(
      code,
      response.status,
      undefined,
      retryAfterSeconds === undefined ? undefined : { retryAfterSeconds },
    );
  }

  return (await response.json()) as T;
}

/** One thing the answer was built from, and how much of it there was. */
export type GroundingSource = { label: string; detail: string | null };

/** Everything Magix read before answering. */
export type Grounding = {
  sources: GroundingSource[];
  product: string | null;
  store: string | null;
};

/**
 * The small, server-authorized vocabulary used by Magix in a product thread.
 *
 * This deliberately does not contain prices.  A proposed image action only
 * carries the work Magix understood; the server calculates its quote and
 * records it with the message before the UI may show a confirmation button.
 */
export type MagixAction =
  | {
      id: string;
      type: 'READ_BRIEF';
      productProjectId: string;
      request: string;
      proposals: number;
      confirmationRequired: false;
    }
  | {
      id: string;
      type: 'WRITE_IDEAS';
      productProjectId: string;
      briefId: string;
      confirmationRequired: false;
    }
  | {
      id: string;
      type: 'GENERATE';
      productProjectId: string;
      planIds: string[];
      ratio: string;
      quantity: number;
      modelProfileId: string;
      confirmationRequired: true;
    }
  | {
      id: string;
      type: 'VARIATIONS';
      productProjectId: string;
      assetId: string;
      quantity: number;
      confirmationRequired: true;
    }
  | {
      id: string;
      type: 'CHANGE';
      productProjectId: string;
      assetId: string;
      direction: string;
      confirmationRequired: true;
    }
  | {
      id: string;
      type: 'RESIZE';
      productProjectId: string;
      assetIds: string[];
      ratio: string;
      confirmationRequired: true;
    }
  | {
      id: string;
      type: 'BUILD_STORE_PAGE';
      productProjectId: string;
      direction: string;
      confirmationRequired: false;
    }
  | { id: string; type: 'ASK'; question: string; confirmationRequired: false };

/** The lifecycle is persisted with the assistant message, not inferred by the UI. */
export type MagixActionStatus =
  'proposed' | 'confirmed' | 'running' | 'completed' | 'failed' | 'cancelled';

/** IDs of work produced by a Magix action.  A refresh can rebuild its card from these. */
export type MagixActionResult = {
  brief?: CreativeBrief;
  briefId?: string;
  planIds?: string[];
  plans?: CreativePlan[];
  assetIds?: string[];
  assets?: LibraryAsset[];
  packId?: string;
  jobIds?: string[];
  errorCode?: string;
};

export type MagixMessageStructuredContent = {
  action?: MagixAction | null;
  status?: MagixActionStatus;
  result?: MagixActionResult | null;
  /** Server quote for a confirmable action. Never supplied by the model. */
  quote?: { totalCredits: number; availableCredits: number; canAfford: boolean } | null;
  errorCode?: string | null;
  context?: { productProjectId: string | null };
};

export async function streamConversationMessage(input: {
  conversationId: string;
  content: string;
  clientMessageId: string;
  signal?: AbortSignal;
  onMeta?: (value: { userMessageId: string }) => void;
  /**
   * What the answer is standing on, sent before the first word of it.
   *
   * Not a reconstruction of the model's reasoning, it is not asked for one
   * but the inventory of what was actually put in front of it. It arrives
   * first so the wait has something in it besides the word "Thinking".
   */
  onGrounding?: (value: Grounding) => void;
  onDelta: (text: string) => void;
  onAction: (action: CopilotAction) => void;
  onDone: (value: { assistantMessageId: string; actions: CopilotAction[] }) => void;
}): Promise<void> {
  const response = await fetch(`/api/conversations/${input.conversationId}/messages`, {
    method: 'POST',
    ...(input.signal ? { signal: input.signal } : {}),
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ content: input.content, clientMessageId: input.clientMessageId }),
  });
  if (!response.ok || !response.body) {
    const body = (await response.json().catch(() => null)) as { error?: { code?: string } } | null;
    throw new ApiError(body?.error?.code ?? 'INTERNAL_ERROR', response.status);
  }
  const reader = response.body.getReader(),
    decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replaceAll('\r\n', '\n');
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf('\n\n');
      let name = 'message',
        raw = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) name = line.slice(6).trim();
        if (line.startsWith('data:')) raw += line.slice(5).trim();
      }
      if (!raw) continue;
      const data = JSON.parse(raw) as Record<string, unknown>;
      if (name === 'meta') input.onMeta?.(data as { userMessageId: string });
      else if (name === 'grounding') input.onGrounding?.(data as unknown as Grounding);
      else if (name === 'delta')
        input.onDelta(typeof data['text'] === 'string' ? data['text'] : '');
      else if (name === 'action') input.onAction(data as CopilotAction);
      else if (name === 'done')
        input.onDone(data as { assistantMessageId: string; actions: CopilotAction[] });
      else if (name === 'error')
        throw new ApiError(
          typeof data['code'] === 'string' ? data['code'] : 'INTERNAL_ERROR',
          500,
          typeof data['message'] === 'string' ? data['message'] : undefined,
        );
    }
  }
}

/**
 * Send a turn in the durable, one-thread-per-product Magix conversation.
 *
 * Kept separate from `streamConversationMessage` while the standalone
 * Copilot still consumes its older multi-action contract.  Both use the same
 * endpoint; the additional body fields are the product-workspace context for
 * the phase-2 route and are harmless to an older server during rollout.
 */
export async function streamProductConversationMessage(input: {
  conversationId: string;
  content: string;
  clientMessageId: string;
  intentHint?: string;
  selectedAssetId?: string | null;
  uiLanguage?: string;
  ratio?: string;
  modelProfileId?: string;
  quantity?: number;
  selectedPlanIds?: string[];
  presetId?: string | null;
  noText?: boolean;
  approvedBriefId?: string;
  signal?: AbortSignal;
  onMeta?: (value: { userMessageId: string }) => void;
  onGrounding?: (value: Grounding) => void;
  onDelta: (text: string) => void;
  onAction?: (action: MagixAction) => void;
  onDone: (value: {
    assistantMessageId: string;
    action: MagixAction | null;
  }) => void | Promise<void>;
}): Promise<void> {
  const response = await fetch(`/api/conversations/${input.conversationId}/messages`, {
    method: 'POST',
    ...(input.signal ? { signal: input.signal } : {}),
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      content: input.content,
      clientMessageId: input.clientMessageId,
      intentHint: input.intentHint,
      selectedAssetId: input.selectedAssetId,
      uiLanguage: input.uiLanguage,
      ratio: input.ratio,
      modelProfileId: input.modelProfileId,
      quantity: input.quantity,
      selectedPlanIds: input.selectedPlanIds,
      presetId: input.presetId,
      noText: input.noText,
      approvedBriefId: input.approvedBriefId,
    }),
  });
  if (!response.ok || !response.body) {
    const body = (await response.json().catch(() => null)) as { error?: { code?: string } } | null;
    throw new ApiError(body?.error?.code ?? 'INTERNAL_ERROR', response.status);
  }

  const reader = response.body.getReader(),
    decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replaceAll('\r\n', '\n');
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf('\n\n');
      let name = 'message',
        raw = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) name = line.slice(6).trim();
        if (line.startsWith('data:')) raw += line.slice(5).trim();
      }
      if (!raw) continue;
      const data = JSON.parse(raw) as Record<string, unknown>;
      if (name === 'meta') input.onMeta?.(data as { userMessageId: string });
      else if (name === 'grounding') input.onGrounding?.(data as unknown as Grounding);
      else if (name === 'delta')
        input.onDelta(typeof data['text'] === 'string' ? data['text'] : '');
      else if (name === 'action') input.onAction?.(data as unknown as MagixAction);
      else if (name === 'done')
        await input.onDone({
          assistantMessageId:
            typeof data['assistantMessageId'] === 'string' ? data['assistantMessageId'] : '',
          action: (data['action'] as MagixAction | null | undefined) ?? null,
        });
      else if (name === 'error')
        throw new ApiError(
          typeof data['code'] === 'string' ? data['code'] : 'INTERNAL_ERROR',
          500,
          typeof data['message'] === 'string' ? data['message'] : undefined,
        );
    }
  }
}

const qs = (params: Record<string, string | undefined>): string =>
  new URLSearchParams(
    Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1])),
  ).toString();

/**
 * Better Auth endpoints.
 *
 * Kept separate from `api` because their error shape is Better Auth's, not our
 * taxonomy, the messages here are already user-facing.
 */
export class AuthCallError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
    this.name = 'AuthCallError';
  }
}

async function authJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`/api/auth/${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string;
      code?: string;
    } | null;
    throw new AuthCallError(
      payload?.message ?? 'That did not work. Check your details and try again.',
      response.status,
      payload?.code ?? null,
    );
  }

  return (await response.json().catch(() => ({}))) as T;
}

async function authCall(path: string, body: unknown): Promise<void> {
  await authJson<unknown>(path, body);
}

/**
 * The part of Better Auth's twoFactor plugin the edge lets through: starting
 * an enrolment, and the sign-in challenge.
 *
 * `trustDevice` is spelled out as false on every verify. The plugin would
 * otherwise accept it from a caller, and "remember this browser" is a feature
 * this product does not offer on any sign-in.
 */
export const twoFactorApi = {
  /** Starts enrolment. The secret only goes live once a code from it is verified. */
  enable: (password: string) =>
    authJson<{ totpURI: string; backupCodes: string[] }>('two-factor/enable', { password }),
  verifyTotp: (code: string) => authCall('two-factor/verify-totp', { code, trustDevice: false }),
  verifyBackupCode: (code: string) =>
    authCall('two-factor/verify-backup-code', { code, trustDevice: false }),
  // Confirming an enrolment, regenerating codes and disabling are not here on
  // purpose: the edge answers 404 for the plugin's versions of them. They go
  // through `api.adminEnrollConfirm`, `adminRecoveryRegenerate` and
  // `adminDisableMfa`, which ask for a code and a reason and are audited.
};

export const authApi = {
  /**
   * `twoFactorRequired` means the password was right and no session exists
   * yet: the account has an authenticator and its code is still to be given.
   */
  signIn: async (email: string, password: string): Promise<{ twoFactorRequired: boolean }> => {
    const answer = await authJson<{ twoFactorRedirect?: boolean }>('sign-in/email', {
      email,
      password,
    });
    return { twoFactorRequired: answer.twoFactorRedirect === true };
  },
  signUp: (email: string, password: string, name: string) =>
    authCall('sign-up/email', { email, password, name }),
  signOut: () => authCall('sign-out', {}),
  requestPasswordReset: (email: string) =>
    authCall('request-password-reset', { email, redirectTo: '/reset-password' }),
  resetPassword: (token: string, newPassword: string) =>
    authCall('reset-password', { token, newPassword }),
};

export type Balance = {
  available: number;
  reserved: number;
  lifetimeSpent: number;
  /**
   * The share of `available` that came from the subscription (plan credits).
   * Optional because an older API answers without it: absent means the page
   * shows one figure and no breakdown.
   */
  plan?: number;
  /** `available` minus `plan`: credits Krevya added, which stay. */
  permanent?: number;
  /** When the plan credits end with the paid term, in ms, or null. */
  planExpiresAt?: number | null;
};

export type LedgerEntry = {
  id: string;
  type: string;
  availableDelta: number;
  reservedDelta: number;
  spentDelta: number;
  reason: string | null;
  createdAt: number;
};

export type GenerationItem = {
  id: string;
  index: number;
  status: string;
  assetId: string | null;
  errorCode: string | null;
  /** What the content filter objected to, when it refused. Categories only. */
  refusedFor?: string[] | null;
  qaStatus: 'pending' | 'passed' | 'failed' | 'not_configured' | 'ungraded' | null;
  /** An in-progress frame exists for this item while it is still being drawn. */
  /**
   * When the in-progress frame was last written, null if there is none.
   *
   * Used as the cache-buster on the preview URL, so a new frame is a new URL
   * and the browser actually refetches. A boolean here is what made the
   * picture stop resolving after its first blurry step.
   */
  previewAt?: number | null;
  /** Video only (docs/video-generation-plan.md §4): a submit/poll counter. */
  pollCount?: number;
  /** Video only: when this item's provider submission was first made. */
  submittedAt?: number | null;
};

export type Generation = {
  id: string;
  status: string;
  quantity: number;
  successCount: number;
  failureCount: number;
  reservedCredits: number;
  spentCredits: number;
  returnedCredits: number;
  ratio: string;
  /** The language the job was ordered in. */
  language?: string;
  productTitle: string;
  creativeSetName: string;
  creativeSetVersion: number;
  modelName: string;
  createdAt: number;
  completedAt: number | null;
  items: GenerationItem[];
  /** 'image' | 'video' (docs/video-generation-plan.md §4). Always present; existing callers ignore it. */
  mediaType: string;
  /** Video only; null for an image job. */
  durationSeconds: number | null;
  /** Video only; null for an image job. */
  resolution: string | null;
  /** Video only; the character used, if any, frozen at job creation time. */
  videoCharacterName: string | null;
  /** 'create' | 'motion_control' | 'edit'; always present, existing callers ignore it. */
  videoMode: string;
  /** motion_control only: the uploaded motion-source video asset. */
  motionVideoAssetId: string | null;
  /** edit only: the uploaded video that was edited. */
  sourceVideoAssetId: string | null;
  /** motion_control only: 'video' | 'image'. */
  characterOrientation: string | null;
  /** The product project the job belongs to. */
  projectId?: string;
  /** What the merchant wrote for this job; null when nothing was written. */
  customDirection?: string | null;
  /** Video only: whether sound was asked for. */
  withAudio?: boolean;
  modelProfileId?: string;
  /** Video only; null without a character, or once that character is deleted. */
  videoCharacterId?: string | null;
  /**
   * The job's first delivered asset, for a row that wants a thumbnail.
   * Only the list route fills it; a single job carries its items instead.
   */
  previewAssetId?: string | null;
  /** The first error code the job's items recorded. List route only. */
  errorCode?: string | null;
};

/** Written to lengths that fit on a picture. Used by the copy suggester. */
export type CopySuggestion = {
  headline: string;
  subheadline: string | null;
  cta: string | null;
};

export type LibraryAsset = {
  assetId: string;
  jobId: string;
  productTitle: string;
  creativeSetName: string;
  ratio: string;
  createdAt: number;
  isMock: boolean;
  /** The concept behind the picture: what to seed more of, and why it argues this. */
  creativePlanId: string | null;
  angle: string | null;
  conceptTitle: string | null;
  rationale: string | null;
  /** 'image' | 'video' | 'audio' (docs/video-generation-plan.md §8). */
  mediaType: 'image' | 'video' | 'audio';
  durationMs: number | null;
  /** What the bytes are, so a download is named with the right extension. */
  mimeType?: string;
  /** The merchant's own labels, alphabetical. */
  tags?: string[];
};

/** One price for one concept in several shapes (`POST /api/generations/ad-set/quote`). */
export type AdSetQuote = {
  ratios: string[];
  quantity: number;
  /** How many ad formats the order spans; 1 when it is only shapes. */
  formats: number;
  totalImages: number;
  creditsPerImage: number;
  totalCredits: number;
  availableCredits: number;
  canAfford: boolean;
};

/** One format of an order: its slug, and the direction written for it. */
export type AdSetVariantInput = { id: string; customDirection: string; noText?: boolean };

/** The words beside the picture, per platform (`POST /api/creative-copy/platform`). */
export type PlatformAdCopy = {
  meta: { primaryText: string; headline: string; description: string | null }[];
  tiktok: { caption: string }[];
  /** True when no model wrote it: a local rehearsal. */
  rehearsal: boolean;
};

/** One admin session, as the server describes it. Times are epoch milliseconds. */
export type AdminSessionInfo = {
  fingerprint: string;
  createdAt: number;
  lastSeenAt: number;
  mfaVerifiedAt: number;
  expiresAt: number;
  idleExpiresAt: number;
};

/**
 * What `/api/admin/session` says about this browser.
 *
 * `enrollment_required` means the account has no authenticator yet,
 * `verification_required` means it has one and no admin session is open, and
 * `active` means the admin area may load.
 */
export type AdminSessionState = {
  state: 'enrollment_required' | 'verification_required' | 'active';
  environment: string;
  email: string;
  mfa: { enrolled: boolean; status: 'none' | 'pending' | 'enrolled' };
  session: AdminSessionInfo | null;
  /** Why a session this browser held is gone, when it held one. */
  sessionEnded: 'expired' | 'revoked' | null;
  stepUpWindowSeconds: number;
};

/** What verifying, a recovery code or confirming an enrolment answers. */
export type AdminVerified = {
  session: AdminSessionInfo;
  method: 'totp' | 'recovery';
  /** How many recovery codes are left; only set when a recovery code was the way in. */
  recoveryCodesRemaining: number | null;
};

/** A row of the sessions list. The fingerprint names a session; it is never its token. */
export type AdminSessionRow = {
  fingerprint: string;
  current: boolean;
  createdAt: number;
  lastSeenAt: number;
  mfaVerifiedAt: number;
  expiresAt: number;
  userAgent: string | null;
  environment: string;
};

export type AdminWorkspace = {
  id: string;
  name: string;
  slug: string;
  balance: Balance;
};
export type ProviderAvailability = {
  provider: string;
  configured: boolean;
  enabled: boolean;
  status: string;
  reason?: string;
};
export type EvalRun = {
  id: string;
  status: string;
  caseSetFingerprint: string;
  selectedCaseCount: number;
  createdAt: number;
  modelName: string;
  provider: string;
  providerModel: string;
};
export type EvalRunItem = {
  eval_case_id: string;
  generation_job_id: string;
  generation_item_id: string | null;
  itemStatus: string | null;
  assetId: string | null;
};
export type EvalComparison = {
  id: string;
  modelName: string;
  provider: string;
  providerModel: string;
  caseCount: number;
  successful: number | null;
  accepted: number | null;
  overall: number | null;
  productFidelity: number | null;
  promptAdherence: number | null;
  textQuality: number | null;
  visualQuality: number | null;
  actualCostMicrousd: number | null;
  unavailableCostCount: number | null;
  dataset: string;
};
export type OperationsSummary = {
  window: string;
  summary: {
    attempts: number;
    successes: number;
    failures: number;
    unknowns: number;
    actualCostMicrousd: number;
    unavailableCostAttempts: number;
  };
  errors: { code: string; count: number }[];
};
/**
 * One generation item holding credits that nothing will ever release on its
 * own. The provider may have been paid for it, so only an operator decides.
 */
export type StrandedItem = {
  itemId: string;
  jobId: string;
  itemIndex: number;
  workspaceId: string;
  workspaceName: string | null;
  ownerEmail: string | null;
  ownerName: string | null;
  productProjectId: string;
  productName: string | null;
  request: {
    mediaType: string;
    ratio: string | null;
    quantity: number | null;
    language: string | null;
    customDirection: string | null;
  };
  strandedAt: number;
  strandedForMs: number;
  creditsHeld: number;
  provider: string | null;
  providerModel: string | null;
  providerTaskId: string | null;
  attemptCount: number;
  errorCode: string | null;
  errorMessage: string | null;
  providerCostMicrousd: number | null;
  providerCostBasis: string | null;
  assetId: string | null;
  candidateObjectKey: string | null;
  previewObjectKey: string | null;
};
export type CopilotAction =
  | {
      id: string;
      type: 'CREATE_CREATIVE_PLANS';
      productProjectId: string;
      count: number;
      ratio: string;
      language: string;
    }
  | { id: string; type: 'OPEN_PRODUCT_PROJECT'; productProjectId: string }
  | { id: string; type: 'OPEN_ASSET'; assetId: string }
  | { id: string; type: 'FILTER_PLANS'; productProjectId: string; blueprintSlugs: string[] }
  | {
      id: string;
      type: 'REFINE_CREATIVE_PLAN';
      productProjectId: string;
      creativePlanId: string;
      instruction: string;
    }
  | {
      id: string;
      type: 'PREPARE_GENERATION';
      productProjectId: string;
      creativePlanIds: string[];
      modelProfileId: string;
      quantityPerPlan: number;
      totalCredits: number;
      confirmationRequired: true;
    };
export type Conversation = {
  id: string;
  workspaceId: string;
  productProjectId: string | null;
  title: string;
  status: string;
  createdAt: number;
  updatedAt: number;
};
export type ConversationMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /**
   * Old Copilot messages may retain `actions`; product Magix messages carry
   * one lifecycle-managed action.  Reading both lets the UI migrate without
   * rewriting a merchant's existing archive.
   */
  structuredContent: null | ({ actions?: CopilotAction[] } & MagixMessageStructuredContent);
  provider: string | null;
  providerModel: string | null;
  createdAt: number;
};
export type CreativePlan = {
  id: string;
  blueprintVersionId: string;
  blueprintId: string;
  title: string;
  rationale: string;
  concept: {
    title: string;
    rationale: string;
    blueprintSlug: string;
    coreInsight: string;
    targetAngle: string;
    hook: string;
    headline: string;
    scene: string;
    composition: string;
    visualDirection: string;
    copyStructure: string;
    productFocus: string;
    referenceUsage: string;
    differentiation: string;
    funnelIntent: 'awareness' | 'consideration' | 'conversion';
    factualClaimIds: string[];
  };
  creativeSpec: {
    copy: { headline?: string; callouts?: string[] };
    composition: string;
  };
};
export type CreativeIntelligenceSummary = {
  productSummary: string;
  productFacts: { id: string; value: string; kind: string; confidence: number }[];
  missingInformation: string[];
  visualSummary: string;
  visualFacts: { id: string; value: string; kind: string; confidence: number }[];
  visualStatus: 'analyzed' | 'limited';
  researchStatus: 'complete' | 'not_configured' | 'limited';
  researchNote: string;
  researchBrief: ResearchBrief | null;
  provider: string;
  providerModel: string;
};
export type CreativeReference = {
  id: string;
  assetId: string;
  referenceType: 'design' | 'competitor';
  semanticRole:
    | 'BRAND_LOGO'
    | 'PRODUCT_REFERENCE'
    | 'STYLE_REFERENCE'
    | 'LAYOUT_REFERENCE'
    | 'LIGHTING_REFERENCE'
    | 'COLOR_REFERENCE'
    | 'TYPOGRAPHY_REFERENCE';
  annotation: string | null;
  createdAt: number;
};
export type TriageCard = {
  assetId: string;
  itemId: string;
  jobId: string;
  createdAt: number;
  ratio: string;
  productTitle: string;
  planId: string | null;
  conceptTitle: string | null;
  hook: string | null;
  angleName: string;
};

export type CreativeBoard = {
  batchId: string;
  /** Written by Krevya between visits rather than asked for. */
  writtenAutomatically?: boolean;
  researchRunId: string;
  directorRunId: string;
  intelligence: CreativeIntelligenceSummary;
  plans: CreativePlan[];
};
/**
 * What Magix understood before it wrote anything.
 *
 * Most merchants cannot name the ad they want, but every one of them can tell
 * you when an assumption is wrong. The brief exists to be corrected.
 */
export type CreativeBrief = {
  audience: string;
  job: 'awareness' | 'consideration' | 'conversion';
  placement: string;
  promise: string;
  proof: string;
  avoid: string[];
  /** Handed to the concept writer verbatim. */
  direction: string;
};

/** What a store pack was written from, so the screen can say it honestly. */
export type StorePackGrounding = {
  level: 'research' | 'listing';
  researchRunId: string | null;
  angle: string | null;
  audience: string | null;
};

/**
 * What starting a store pack would cost. The pack's own writing is text and
 * costs no credits; research is charged once per product and reused after.
 */
export type StorePackQuote = {
  pack: number;
  research: {
    credits: number;
    /** A saved run stands in: already researched, included. */
    reusable: boolean;
    mode: string;
    /** A fresh run reads the competitor pages it is given, and needs one. */
    needsCompetitorUrl: boolean;
  };
  total: number;
  availableCredits: number;
  canAfford: boolean;
  /** What a pack started with the same market and language would really use. */
  market: string | null;
  language: string;
  currency: string;
};

/** A storefront writing run. The summary is what the Memory list keeps; the
 * snapshot adds the individual sections once a merchant opens a run. */
export type StorePackSummary = {
  id: string;
  workspaceId: string;
  productProjectId: string;
  status: 'running' | 'complete' | 'failed';
  stage: 'brief' | 'copy' | 'assemble' | null;
  errorCode: string | null;
  params: {
    audience: {
      awareness: 1 | 2 | 3 | 4 | 5;
      hardness: number;
      gender: 'female' | 'male' | 'mixed' | null;
      ageFrom: number | null;
      ageTo: number | null;
    };
    market: string | null;
    language: string;
    extraDirection: string | null;
    /** Whether the published storefront claims the reviews are from verified buyers. */
    reviewsVerified?: boolean;
    /** What the pack was really written from. Older packs read as `listing`. */
    grounding?: StorePackGrounding;
  };
  createdAt: number;
  updatedAt: number;
};

export type StorePackPart = {
  kind:
    | 'identity'
    | 'hero_images'
    | 'product_copy'
    | 'secondary_copy'
    | 'faq'
    | 'reviews'
    | 'stats'
    | 'legal'
    | 'popup'
    | 'storefront';
  status: 'pending' | 'running' | 'ready' | 'failed';
  payload: unknown;
  generatedBy: string | null;
  errorCode: string | null;
  updatedAt: number;
};

export type StorePackSnapshot = { pack: StorePackSummary; parts: StorePackPart[] };

export type StorePackImageSlot = {
  id: string;
  slotKey: string;
  labelTr: string;
  labelEn: string;
  ratio: string;
  status: 'empty' | 'running' | 'ready' | 'failed' | 'skipped';
  assetId?: string | null;
  errorCode?: string | null;
};
/**
 * The theme editor's edits for one store pack.
 *
 * The same shape `@krevya/shared`'s `ThemeEdits` has, which is what the
 * deployment lays over the templates it uploads, so what the editor shows is
 * what the draft receives.
 */
export type StorePackThemeSectionEdit = {
  settings: Record<string, unknown>;
  blocks: { type: string; settings: Record<string, unknown> }[];
  disabled?: boolean;
};
export type StorePackThemeStructure = {
  order: string[] | null;
  added: { key: string; type: string }[];
  removed: string[];
};
/** One small change the assistant proposes, with a line saying why. */
export type ThemeAssistStep =
  | {
      kind: 'setting';
      sectionKey: string;
      id: string;
      value: string | number | boolean | null;
      note: string;
    }
  | {
      kind: 'blockSetting';
      sectionKey: string;
      blockIndex: number;
      id: string;
      value: string | number | boolean | null;
      note: string;
    }
  | {
      kind: 'blockAdd';
      sectionKey: string;
      blockType: string;
      settings: Record<string, string | number | boolean | null>;
      note: string;
    }
  | { kind: 'blockRemove'; sectionKey: string; blockIndex: number; note: string }
  | { kind: 'sectionVisibility'; sectionKey: string; hidden: boolean; note: string }
  | { kind: 'themeSetting'; id: string; value: string | number | boolean | null; note: string }
  /** The template's sections, in the order the assistant thinks sells best. */
  | { kind: 'sectionOrder'; order: string[]; note: string };

/** A place a generated picture would help, and what it should show. */
export type ThemeAssistImageSuggestion = { sectionKey: string; settingId: string; brief: string };

export type StorePackThemeEdits = {
  overrides: Record<string, StorePackThemeSectionEdit>;
  structures: { home: StorePackThemeStructure; product: StorePackThemeStructure };
  /** Theme-wide settings the merchant changed: only the ids they changed. */
  themeSettings?: Record<string, unknown>;
  /** What a section was renamed to, by section key. */
  names?: Record<string, string>;
};

export type StorePackImages = {
  revision: number;
  copyHash: string;
  slots: StorePackImageSlot[];
  stale: boolean;
};
export type StorePackImageQuote = {
  quote: { totalCredits: number; availableCredits: number; canAfford: boolean };
  revision: number;
  copyHash: string;
};

export type ThemeDeploymentStatus =
  | 'queued'
  | 'creating'
  | 'processing'
  | 'writing'
  | 'preview_ready'
  | 'publishing'
  | 'published'
  | 'rolling_back'
  | 'rolled_back'
  | 'failed';

export type ThemeDeployment = {
  /** What the last run could not carry to Shopify: a picture that would not
   *  upload, a file of a kind Shopify refuses. Empty when everything went. */
  warnings?: { code: string; detail: string }[];
  id: string;
  productProjectId: string;
  storePackId: string;
  shopifyThemeId: string | null;
  status: ThemeDeploymentStatus;
  previewUrl: string | null;
  errorCode: string | null;
  createdAt: number;
  updatedAt: number;
  publishedAt: number | null;
  canRollback: boolean;
  /** Whether publishing puts the product on its own template by itself. */
  assignsProductTemplate?: boolean;
};
export type CreativePlanRevision = {
  id: string;
  instruction: string;
  before: CreativePlan['concept'];
  after: CreativePlan['concept'];
  createdAt: number;
};
export type OnboardingStep = { key: string; title: string; complete: boolean; path: string };

/**
 * Cursor pagination, client half.
 *
 * `cursor` is opaque: it comes from a previous response's `nextCursor` and is
 * passed back verbatim. `nextCursor === null` means the list is exhausted.
 */
/** What research turned up. Shown to the merchant, not just fed to the director. */
export type ResearchBrief = {
  status: 'complete' | 'not_configured' | 'limited';
  categoryPatterns: string[];
  buyerMotivations: string[];
  commonObjections: string[];
  competitorPatterns: string[];
  overusedDirections: string[];
  whitespaceOpportunities: string[];
  /**
   * Who buys this product, from the research run itself. Empty on a brief
   * written before research could answer it, so read it defensively.
   */
  audiences: { name: string; why: string }[];
  sources: { url: string; title: string; sourceType: string; fetchedAt: number }[];
  note: string;
};

/** The kind of argument an angle makes, which its card names. */
export type AngleType =
  | 'pain_point'
  | 'desired_outcome'
  | 'product_benefit'
  | 'differentiation'
  | 'routine_use_case'
  | 'emotional_premium'
  | 'objection';

/**
 * A strategy for an ad, written as a way to sell.
 *
 * `finding` is what the screen keys the angle on: the research finding it
 * rests on, word for word, which keeps an order grouped under its angle after
 * a reload, or the strategy's own idea when it rests on product facts alone
 * (`source: 'product'`). Only `claim` states what the product does, and a
 * claim always carries the product facts in `evidence`.
 *
 * The strategy fields are absent on a set written before the server chose
 * angles by kind; such a set is written again the next time it is asked for.
 */
export type ResearchAngle = {
  source: 'motivation' | 'objection' | 'opportunity' | 'product';
  finding: string;
  title: string;
  support: string;
  stance: 'claim' | 'question' | 'demonstration' | 'comparison';
  evidence: string[];
  angleType?: AngleType;
  /** The research findings it rests on, word for word. */
  findings?: string[];
};

/**
 * The angles for one research run, in one language.
 *
 * `writing`: another request is writing them and the screen asks again
 * shortly. `failed` and `unavailable`: there are none to show, and the raw
 * findings are deliberately not shown in their place.
 */
export type ResearchAngleSet = {
  status: 'ready' | 'writing' | 'failed' | 'unavailable';
  language: string;
  /**
   * What the angles were built from: research findings, or the product facts
   * alone when research found nothing. The screen names the group by it.
   * Absent on a server that predates it.
   */
  basis?: 'research' | 'product';
  angles: ResearchAngle[];
  /**
   * Who the ads are for, named in `language`, index-matched to the research's
   * audiences. Absent on a server that predates it, and empty until `ready`.
   */
  audiences?: string[];
  provenance: {
    researchRunId: string;
    competitorPages: number;
    categoryPages: number;
    otherPages: number;
  };
};

export type ProductIntelligenceView = {
  product: {
    title: string;
    category: string | null;
    summary: string;
    imageUrls: string[];
    prohibitedClaims: string[];
    missingInformation: string[];
    merchantFacts: string[];
    fromStore: string[];
    observed: string[];
    inferred: string[];
  };
  visual: {
    status: 'analyzed' | 'limited';
    summary: string;
    observed: string[];
    missingInformation: string[];
    analyzedAt: number | null;
  };
  corrections: { id: string; factKey: string; value: string; updatedAt: number }[];
  research: {
    id: string;
    status: string;
    mode: string;
    createdAt: number;
    brief: ResearchBrief | null;
  } | null;
};

/** Three separate readiness questions with three separate answers. */
export type CreativeReadiness = {
  research: {
    searchConfigured: boolean;
    competitorUrlsSupported: boolean;
    available: boolean;
    note: string;
  };
  director: { provider: string; configured: boolean };
  image: {
    slug: string;
    displayName: string;
    provider: string;
    qualityTier: string;
    creditPerImage: number;
    configured: boolean;
  }[];
};

/** Everything behind one delivered creative, joined back up. */
export type AssetProvenance = {
  asset: {
    id: string;
    createdAt: number;
    ratio: string;
    language: string;
    modelName: string;
    creativeSetName: string;
    productTitle: string;
    projectId: string;
    jobId: string;
  };
  concept: {
    planId: string | null;
    title: string | null;
    hook: string | null;
    headline: string | null;
    scene: string | null;
    composition: string | null;
    rationale: string | null;
    coreInsight: string | null;
    targetAngle: string | null;
    funnelIntent: string | null;
  } | null;
  direction: string | null;
  /** What one more of this costs now; null when it cannot be run again. */
  regenerateCredits?: number | null;
  prompt: string | null;
  systemPrompt: string | null;
  research: {
    mode: string | null;
    status: string | null;
    note: string;
    findings: string[];
    sources: { url: string; title: string }[];
  } | null;
};

export type BrandKit = {
  id: string;
  name: string;
  isDefault: boolean;
  logoAssetId: string | null;
  primaryColor: string;
  secondaryColor: string | null;
  palette: string[];
  tone: string | null;
  typography: string | null;
  defaultLanguage: string;
  bannedClaims: string[];
  preferredStyles: string[];
  notes: string | null;
  /** Where a kit drafted from a site came from. Set by the server, never editable. */
  sourceUrl: string | null;
  draftedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

export type BrandKitInput = {
  name?: string;
  primaryColor?: string;
  secondaryColor?: string;
  palette?: string[];
  tone?: string;
  typography?: string;
  defaultLanguage?: string;
  bannedClaims?: string[];
  preferredStyles?: string[];
  notes?: string;
  isDefault?: boolean;
};

export type ResearchMode = 'quick' | 'standard' | 'deep';

/** A saved, already-paid-for research run that a new board can be built on. */
export type ResearchRun = {
  id: string;
  /** Present when the list was not already scoped to one project. */
  projectId?: string;
  productTitle?: string;
  errorCode?: string | null;
  batchId?: string | null;
  status: 'complete' | 'limited' | 'not_configured' | 'failed' | 'running';
  mode: ResearchMode;
  provider: string;
  createdAt: number;
  completedAt: number | null;
  creditsCharged: number;
  sourceRunId: string | null;
  sourceCount: number;
  /** How many runs this product has had. Only sent for `view: 'latest'`. */
  runCount?: number;
};

/** A competitor's product as its own storefront publishes it. */
export type CompetitorStoreProduct = {
  title: string;
  handle: string;
  url: string;
  price: number | null;
  compareAtPrice: number | null;
  imageUrl: string | null;
  publishedAt: string | null;
};

/** Mirrors `CompetitorStoreAnalysis` in packages/core/src/creative/competitor-store.ts. */
export type CompetitorStoreAnalysis = {
  version: 1;
  host: string;
  origin: string;
  storeName: string | null;
  platform: 'shopify' | 'unknown';
  fetchedAt: number;
  catalogue: {
    productCount: number;
    countIsFloor: boolean;
    priceMin: number | null;
    priceMax: number | null;
    priceMedian: number | null;
    currency: string | null;
    onSaleShare: number | null;
    averageDiscountPercent: number | null;
    topProductTypes: { name: string; count: number }[];
    topVendors: { name: string; count: number }[];
  } | null;
  bestSellers: {
    source: 'best_selling_page' | 'catalogue_order' | 'none';
    products: CompetitorStoreProduct[];
  };
  newest: {
    products: CompetitorStoreProduct[];
    last30Days: number;
    last90Days: number;
    perMonth: number | null;
    latestAt: string | null;
  };
  theme: { name: string | null; schemaName: string | null; themeStoreId: number | null } | null;
  apps: { name: string; category: string }[];
  offers: { kind: string; text: string }[];
  reviews: { app: string | null; count: number | null; rating: number | null } | null;
  productPage: { url: string; title: string } | null;
  links: { metaAdLibrary: string; googleAdsTransparency: string; tiktokCreativeCenter: string };
  notes: string[];
};

/** One row of the workspace's saved competitor list. */
export type CompetitorStore = {
  id: string;
  host: string;
  url: string;
  label: string | null;
  status: 'complete' | 'failed';
  errorCode: string | null;
  projectId: string | null;
  researchRunId: string | null;
  storeName: string | null;
  productCount: number | null;
  createdAt: number;
  updatedAt: number;
  analysis?: CompetitorStoreAnalysis | null;
};

export type ResearchTask = {
  stage:
    | 'product_analysis'
    | 'category_research'
    | 'competitor_discovery'
    | 'competitor_analysis'
    | 'reference_analysis'
    | 'synthesis';
  status: 'pending' | 'running' | 'completed' | 'partial' | 'failed';
  completed: number;
  total: number;
  errorCode: string | null;
  updatedAt: number;
};

/**
 * The customer panel's shapes.
 *
 * `billing.configured` is false on a deployment with no Whop key, and the
 * panel says so rather than showing a revenue box reading zero, zero reads as
 * "nobody bought anything" instead of "nobody can". Revenue is a list, one
 * entry per currency: summing across currencies would produce a number that
 * means nothing.
 */
export type AdminOverview = {
  window: string;
  people: { total: number; recent: number };
  workspaces: { total: number; recent: number; active: number };
  jobs: { total: number; completed: number; partial: number; failed: number };
  images: { total: number; succeeded: number; failed: number };
  credits: { granted: number; spent: number };
  providerCostMicrousd: number;
  imageLatencyMs: { p50: number | null; p95: number | null };
  signupsByDay: { day: string; value: number }[];
  busiestAccounts: { workspaceId: string; workspaceName: string; jobs: number; images: number }[];
  billing: {
    configured: boolean;
    revenue: { currency: string; amount: number; invoices: number; payers: number }[];
    activeSubscriptions: number;
    subscriptionsByPlan: { planId: string | null; status: string; value: number }[];
  };
};

/** Money in minor units, and its own currency. */
export type Money = { currency: string; amount: number; invoices?: number };

export type Subscription = {
  id: string;
  workspaceId: string;
  /** Whop's membership id (mem_…). */
  providerSubscriptionId: string;
  /**
   * Whop's page for this membership: card, receipts, cancelling. Null on a
   * row written before Whop said where it is.
   */
  manageUrl: string | null;
  planId: string | null;
  lookupKey: string | null;
  status: string;
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: number | null;
  /** 'year' for an annual plan. Absent or null reads as monthly. */
  interval?: 'month' | 'year' | null;
  /** On a yearly plan: when the next monthly grant of credits arrives, in ms. */
  nextGrantAt?: number | null;
  /** The currency the plan was bought in; renewals and a switch stay in it. */
  currency?: string | null;
};

export type Invoice = {
  id: string;
  /** Whop's payment id (pay_…). */
  providerPaymentId: string;
  number: string | null;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  periodStart: number | null;
  periodEnd: number | null;
  paidAt: number | null;
  creditsGranted: number;
  createdAt: number;
};

/**
 * The landing page's prices. Defined in `@krevya/shared` beside the fallback
 * table so the two cannot describe different shapes.
 */
export type { PublicPlan, PublicPrice } from '@krevya/shared';

export type PlanOffer = {
  id: string;
  name: string;
  credits: number;
  /** How many standard creatives this plan's credits buy, counted by the server. */
  creativesPerMonth: number;
  highlight: boolean;
  available: boolean;
  /**
   * False for a plan arranged by agreement (Partner): it is shown with a way
   * to get in touch, never a checkout button. Absent from older answers.
   */
  selfServe?: boolean;
  /** Stores the plan may have connected at once; null is unlimited. */
  storeLimit?: number | null;
  /**
   * False when Whop has no yearly plan to sell at the shown yearly price (a
   * list price stands in for it). Absent from older answers.
   */
  availableYear?: boolean;
  /** The monthly amount in minor units. Null when the plan has no live price. */
  amount: number | null;
  currency: string | null;
  interval: string | null;
  /**
   * The plan's price per billing interval, in minor units, null where Whop
   * has none. `amount`/`interval` above stay the monthly price for older
   * readers; the page prefers these.
   */
  prices?: { month: number | null; year: number | null };
  /** How much cheaper a year is than twelve months, in percent. */
  savingsPercent?: number | null;
};

export type AdminCustomer = {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: number;
  ownerEmail: string | null;
  ownerName: string | null;
  members: number;
  credits: { available: number; reserved: number; lifetimeSpent: number };
  generations: { jobs: number; succeeded: number; failed: number };
  providerCostMicrousd: number;
  lastActiveAt: number | null;
  subscription: { planId: string | null; status: string } | null;
  paid: Money[];
};

export type AdminCustomerDetail = {
  workspace: Omit<
    AdminCustomer,
    | 'members'
    | 'credits'
    | 'generations'
    | 'providerCostMicrousd'
    | 'lastActiveAt'
    | 'subscription'
    | 'paid'
  >;
  subscription: Subscription | null;
  invoices: Invoice[];
  invoicesNextCursor: string | null;
  invoicesTotal: number;
  jobsNextCursor: string | null;
  jobsTotal: number;
  boardsNextCursor: string | null;
  boardsTotal: number;
  ledgerNextCursor: string | null;
  ledgerTotal: number;
  paid: Money[];
  credits: { available: number; reserved: number; lifetimeSpent: number };
  members: { userId: string; name: string; email: string; role: string; joinedAt: number }[];
  jobs: {
    id: string;
    status: string;
    quantity: number;
    ratio: string;
    language: string;
    credits: number;
    succeeded: number;
    failed: number;
    createdAt: number;
    startedAt: number | null;
    completedAt: number | null;
    userEmail: string | null;
    productTitle: string | null;
  }[];
  boards: {
    id: string;
    status: string;
    mode: string;
    credits: number;
    boardErrorCode: string | null;
    createdAt: number;
    completedAt: number | null;
  }[];
  ledger: {
    id: string;
    type: string;
    reason: string | null;
    availableDelta: number;
    reservedDelta: number;
    spentDelta: number;
    createdAt: number;
  }[];
  integrations: {
    provider: string;
    storeDomain: string | null;
    displayName: string | null;
    status: string;
    createdAt: number;
  }[];
  errors: { code: string; count: number }[];
  imageLatencyMs: { p50: number | null; p95: number | null };
};

/**
 * One ordered history, not two lists to interleave.
 *
 * The server merges generations and boards before paging them: a cursor into
 * two separately-paged lists cannot be correct, because the page boundary
 * falls inside one of them and the rows past it in the other are either
 * dropped or served twice.
 */
export type AdminActivityEvent = {
  kind: 'generation' | 'board';
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  status: string;
  credits: number;
  createdAt: number;
  completedAt: number | null;
  /** Generations only. */
  userEmail?: string | null;
  quantity?: number;
  ratio?: string;
  startedAt?: number | null;
  succeeded?: number;
  failed?: number;
  productTitle?: string | null;
  /** Boards only. */
  mode?: string;
  boardErrorCode?: string | null;
};

export type AdminActivity = { events: AdminActivityEvent[]; nextCursor: string | null };

/** The Creative Director's passes: decide, draft, critique, rewrite, rank. */
export type DirectorPass = {
  stage: 'strategy' | 'ideation' | 'review';
  status: 'pending' | 'running' | 'completed' | 'failed';
  detail: Record<string, unknown> | null;
  updatedAt: number;
};

/**
 * What the model is writing at this instant.
 *
 * A pass took the better part of a minute and showed nothing at all until it
 * had finished, which is what made a board feel dead rather than slow. `line`
 * is the sentence being written; `titles` are the concept names that have
 * finished arriving.
 */
export type BoardThinking = {
  stage: 'strategy' | 'ideation' | 'review';
  line: string;
  titles: string[];
};

export type ResearchRunProgress = {
  brief: ResearchBrief | null;
  id: string;
  status: 'running' | 'complete' | 'limited' | 'not_configured' | 'failed';
  provider: string;
  mode: ResearchMode;
  errorCode: string | null;
  createdAt: number;
  completedAt: number | null;
  batchId: string | null;
  creditsCharged: number;
  sourceRunId: string | null;
  directorStatus: 'running' | 'complete' | 'failed' | null;
};

export type BoardStart = {
  researchRunId: string;
  status: 'running';
  strategy: 'none' | 'reuse' | 'fresh';
  creditsCharged: number;
};

export type LibraryFilters = {
  search?: string | undefined;
  ratio?: string | undefined;
  projectId?: string | undefined;
  /** Defaults to 'image' server-side. */
  media?: 'image' | 'video' | 'audio' | undefined;
  /** One of the merchant's own labels. */
  tag?: string | undefined;
};

export type PageQuery = { limit?: number | undefined; cursor?: string | null | undefined };

function pageQs(page: PageQuery | undefined): Record<string, string | undefined> {
  if (!page) return {};
  return {
    ...(page.limit ? { limit: String(page.limit) } : {}),
    ...(page.cursor ? { cursor: page.cursor } : {}),
  };
}

/** Finds the active durable Magix thread for one product, if it already exists. */
export async function findProductConversation(
  workspaceId: string,
  productProjectId: string,
): Promise<Conversation | null> {
  const result = await call<{ conversations: Conversation[]; nextCursor: string | null }>(
    `/api/conversations?${qs({ workspaceId, productProjectId })}`,
  );
  // Filtering locally as well means this remains safe against a rolling
  // deployment where the API has not started honoring productProjectId yet.
  return (
    result.conversations.find(
      (conversation) => conversation.productProjectId === productProjectId,
    ) ?? null
  );
}

/**
 * Returns the sole active conversation for this product, creating it only
 * when none exists.  The server remains the race-safe authority: its create
 * route must enforce the one-active-conversation invariant.
 */
export async function findOrCreateProductConversation(
  workspaceId: string,
  productProjectId: string,
): Promise<Conversation> {
  const existing = await findProductConversation(workspaceId, productProjectId);
  if (existing) return existing;
  const created = await call<{ conversation: Conversation }>('/api/conversations', {
    method: 'POST',
    body: JSON.stringify({ workspaceId, productProjectId }),
  });
  return created.conversation;
}

/** Echo the server quote to protect against price changes; the server recalculates it. */
export function confirmConversationAction(
  conversationId: string,
  messageId: string,
  expectedTotalCredits: number,
): Promise<{ message: ConversationMessage }> {
  return call<{ message: ConversationMessage }>(
    `/api/conversations/${conversationId}/messages/${messageId}/confirm`,
    { method: 'POST', body: JSON.stringify({ expectedTotalCredits }) },
  );
}

/* -------------------------------------------------------------------------- */
/* Support (docs/support/PLAN.md)                                             */
/* -------------------------------------------------------------------------- */

/** The department the merchant picks, in hosting-panel terms. */
export const SUPPORT_CATEGORIES = ['billing', 'creatives', 'store', 'account', 'other'] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

/** Whose turn it is. `open` means staff owe an answer, `answered` means you do. */
export type SupportStatus = 'open' | 'answered' | 'on_hold' | 'resolved' | 'closed';
export type SupportPriority = 'low' | 'normal' | 'high' | 'urgent';

export type SupportAttachment = {
  id: string;
  messageId: string;
  filename: string;
  mimeType: string;
  byteSize: number;
};

export type SupportMessage = {
  id: string;
  ticketId: string;
  authorUserId: string;
  authorRole: 'merchant' | 'staff';
  /**
   * `internal` never arrives on a merchant route: the server's merchant query
   * carries `visibility='public'` and takes no argument that could widen it.
   * It is in the type because the staff screen renders both.
   */
  visibility: 'public' | 'internal';
  /** Plain text. Printed as a text node; never handed to dangerouslySetInnerHTML. */
  body: string;
  createdAt: number;
  attachments: SupportAttachment[];
};

export type SupportTicket = {
  id: string;
  workspaceId: string;
  createdByUserId: string;
  subject: string;
  category: SupportCategory;
  status: SupportStatus;
  priority: SupportPriority;
  assignedToUserId: string | null;
  lastActor: 'merchant' | 'staff';
  createdAt: number;
  updatedAt: number;
  lastActivityAt: number;
};

export type StaffSupportTicket = SupportTicket & {
  workspaceName: string | null;
  publicMessageCount: number;
};

/**
 * One product's journey through the loop, as far as it has come.
 *
 * Every flag is a row that exists, not a guess: `researched` means a research
 * run finished, `liveInShopify` means a theme was published. `adsFromHere`
 * counts only ads whose creative Krevya published, because that asset id is
 * the only honest link between an ad and a product.
 */
export type HomeProduct = {
  productId: string;
  title: string;
  imageUrl: string | null;
  projectId: string | null;
  researched: boolean;
  storePageWritten: boolean;
  storePageRunning: boolean;
  draftInShopify: boolean;
  liveInShopify: boolean;
  pictures: number;
  videos: number;
  adsFromHere: number;
  lastActivityAt: number | null;
};

export type HomeEvent = {
  kind: 'research' | 'store_page' | 'draft' | 'live' | 'pictures' | 'videos' | 'ad_sync';
  at: number;
  count: number | null;
  productTitle: string | null;
  projectId: string | null;
  detail: string | null;
};

export type HomeAdsWindow = {
  spendMicros: number;
  impressions: number;
  clicks: number;
  purchases: number;
  revenueMicros: number;
  /** Days in the window that carry a row. Zero means the figures are unknown. */
  days: number;
};

export type HomeOverview = {
  workspace: { id: string; name: string };
  credits: { available: number; reserved: number } | null;
  store: { connected: boolean; domain: string | null; productCount: number };
  totals: {
    productsResearched: number;
    productsWithStorePage: number;
    productsLive: number;
    pictures: number;
    videos: number;
    adsFromHere: number;
    productsWithPictures: number;
    productsOutstanding: number;
  };
  products: HomeProduct[];
  productTotal: number;
  activity: HomeEvent[];
  ads: {
    platform: string;
    accountName: string;
    currency: string | null;
    since: string;
    until: string;
    lastSyncedAt: number | null;
    current: HomeAdsWindow;
    previous: HomeAdsWindow | null;
  } | null;
  /** `key` is a translation key; the server never writes merchant copy. */
  steps: { key: string; href: string; productTitle: string | null }[];
  firstRun: boolean;
};

/** The one multipart shape both merchant writes share. */
async function supportForm<T>(
  path: string,
  workspaceId: string,
  files: File[],
  fields: Record<string, string>,
): Promise<T> {
  const form = new FormData();
  form.set('workspaceId', workspaceId);
  for (const [key, value] of Object.entries(fields)) form.set(key, value);
  for (const file of files) form.append('file', file);
  const response = await fetch(path, { method: 'POST', body: form });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: { code?: string } } | null;
    throw new ApiError(body?.error?.code ?? 'INTERNAL_ERROR', response.status);
  }
  return (await response.json()) as T;
}

export const api = {
  /** Everything the home screen shows, in one round trip. */
  home: (workspaceId: string) => call<HomeOverview>(`/api/home?${qs({ workspaceId })}`),
  onboarding: (workspaceId: string) =>
    call<{ steps: OnboardingStep[]; completed: number; total: number; nextPath: string }>(
      `/api/onboarding?${qs({ workspaceId })}`,
    ),
  conversations: (workspaceId: string, page?: PageQuery) =>
    call<{ conversations: Conversation[]; nextCursor: string | null }>(
      `/api/conversations?${qs({ workspaceId, ...pageQs(page) })}`,
    ),
  conversation: (id: string) =>
    call<{ conversation: Conversation; messages: ConversationMessage[] }>(
      `/api/conversations/${id}`,
    ),
  createConversation: (workspaceId: string, productProjectId?: string) =>
    call<{ conversation: Conversation }>('/api/conversations', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, productProjectId }),
    }),
  findProductConversation,
  findOrCreateProductConversation,
  confirmConversationAction,
  archiveConversation: (id: string) =>
    call<{ status: string }>(`/api/conversations/${id}`, { method: 'DELETE' }),
  /**
   * Starts a board. Answers immediately with a run id to poll, the work
   * itself takes far longer than a request should be held open for.
   *
   * `researchRunId` reuses saved research (free); `runResearch` buys a fresh
   * run; neither means no research at all.
   */
  startCreativeBoard: (input: {
    workspaceId: string;
    productProjectId: string;
    count: number;
    ratio: string;
    language: string;
    customDirection?: string;
    researchMode?: ResearchMode;
    researchRunId?: string;
    runResearch?: boolean;
    competitorUrls?: string[];
    /** The merchant's own product page, read for claims, offers and proof. */
    productUrl?: string;
    /** One angle for the whole board. Omitted means every angle, as before. */
    blueprintSlug?: string;
  }) =>
    call<BoardStart>('/api/creative-plans', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  projectIntelligence: (workspaceId: string, projectId: string) =>
    call<ProductIntelligenceView>(`/api/projects/${projectId}/intelligence?${qs({ workspaceId })}`),

  brandKits: (workspaceId: string) =>
    call<{ brandKits: BrandKit[] }>(`/api/brand-kits?${qs({ workspaceId })}`),

  createBrandKit: (workspaceId: string, input: BrandKitInput) =>
    call<{ brandKit: BrandKit }>('/api/brand-kits', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, ...input }),
    }),

  /**
   * Uploads the brand's mark.
   *
   * Multipart, not JSON: the merchant has a file. Until this existed the
   * column was written by nothing, so "use my logo" could not be answered.
   */
  uploadBrandKitLogo: async (
    workspaceId: string,
    brandKitId: string,
    file: File,
  ): Promise<{ logoAssetId: string }> => {
    const form = new FormData();
    form.set('workspaceId', workspaceId);
    form.set('file', file);
    // Raw fetch, not `call`: that helper forces a JSON content-type, and a
    // multipart body needs the browser to set the header with its boundary.
    const response = await fetch(`/api/brand-kits/${brandKitId}/logo`, {
      method: 'POST',
      body: form,
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      throw new ApiError(body?.error?.code ?? 'INTERNAL_ERROR', response.status);
    }
    return (await response.json()) as { logoAssetId: string };
  },

  removeBrandKitLogo: (workspaceId: string, brandKitId: string) =>
    call<{ logoAssetId: null }>(`/api/brand-kits/${brandKitId}/logo?${qs({ workspaceId })}`, {
      method: 'DELETE',
    }),

  /** Drafts a kit from the merchant's own site. Every value is a suggestion. */
  createBrandKitFromUrl: (workspaceId: string, url: string) =>
    call<{
      brandKit: BrandKit;
      source: {
        url: string;
        logoUrl: string | null;
        detectedColors: string[];
        themeColor: string | null;
      };
    }>('/api/brand-kits/from-url', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, url }),
    }),

  updateBrandKit: (workspaceId: string, brandKitId: string, input: BrandKitInput) =>
    call<{ brandKit: BrandKit }>(`/api/brand-kits/${brandKitId}`, {
      method: 'PATCH',
      body: JSON.stringify({ workspaceId, ...input }),
    }),

  deleteBrandKit: (workspaceId: string, brandKitId: string) =>
    call<{ deleted: boolean }>(`/api/brand-kits/${brandKitId}?${qs({ workspaceId })}`, {
      method: 'DELETE',
    }),

  /** A product entered by hand, no store connection required. */
  createManualProduct: (input: {
    workspaceId: string;
    title: string;
    description?: string;
    vendor?: string;
    productType?: string;
    imageUrls: string[];
  }) =>
    call<{ product: Product }>('/api/products/manual', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  creativeReadiness: (workspaceId: string) =>
    call<CreativeReadiness>(`/api/creative-readiness?${qs({ workspaceId })}`),

  creativeResearchRuns: (
    workspaceId: string,
    projectId?: string,
    page?: PageQuery,
    /** `latest`: one row per product. `all` with a project: its whole history, failures too. */
    view?: 'latest' | 'all',
  ) =>
    call<{
      runs: ResearchRun[];
      nextCursor: string | null;
      total: number;
      pricing: Record<ResearchMode, number>;
    }>(`/api/creative-research-runs?${qs({ workspaceId, projectId, view, ...pageQs(page) })}`),

  competitorStores: (workspaceId: string, projectId?: string) =>
    call<{ stores: CompetitorStore[] }>(`/api/competitor-stores?${qs({ workspaceId, projectId })}`),

  competitorStore: (id: string) => call<{ store: CompetitorStore }>(`/api/competitor-stores/${id}`),

  /** Free: reads what the shop itself publishes. `refresh` skips the one-day cache. */
  analyzeCompetitorStore: (input: {
    workspaceId: string;
    url: string;
    productProjectId?: string | null;
    refresh?: boolean;
  }) =>
    call<{ store: CompetitorStore; fresh: boolean }>('/api/competitor-stores', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateCompetitorStore: (id: string, input: { productProjectId?: string | null }) =>
    call<{ ok: true }>(`/api/competitor-stores/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  deleteCompetitorStore: (id: string) =>
    call<{ ok: true }>(`/api/competitor-stores/${id}`, { method: 'DELETE' }),

  creativeResearchRun: (researchRunId: string) =>
    call<{
      run: ResearchRunProgress;
      tasks: ResearchTask[];
      directorPasses: DirectorPass[];
      draftedConcepts: CreativePlan['concept'][];
      thinking: BoardThinking | null;
    }>(`/api/creative-research-runs/${researchRunId}`),
  /**
   * The angles a run's findings become, written on first need and free: the
   * research they come from was paid for when it ran.
   */
  researchAngles: (researchRunId: string, language: string) =>
    call<ResearchAngleSet>(`/api/creative-research-runs/${researchRunId}/angles`, {
      method: 'POST',
      body: JSON.stringify({ language }),
    }),
  latestCreativeBoard: (workspaceId: string, productProjectId: string) =>
    // `activeRunId` is a board still running for this project. A board is a
    // job and keeps going with nobody watching, so the studio uses this to
    // rejoin one on load rather than showing a page with no sign of it.
    call<{ board: CreativeBoard | null; activeRunId: string | null }>(
      `/api/creative-plans?${qs({ workspaceId, productProjectId })}`,
    ),
  creativeBrief: (input: {
    workspaceId: string;
    productProjectId: string;
    request: string;
    ratio: string;
  }) =>
    call<{ brief: CreativeBrief }>('/api/creative-briefs', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  refineCreativePlan: (workspaceId: string, creativePlanId: string, instruction: string) =>
    call<{ planId: string; revisionId: string }>(`/api/creative-plans/${creativePlanId}`, {
      method: 'PATCH',
      body: JSON.stringify({ workspaceId, instruction }),
    }),
  signalCreativePlan: (
    workspaceId: string,
    creativePlanId: string,
    signal: 'selected' | 'rejected' | 'more_like' | 'less_like' | 'different_direction',
    metadata?: Record<string, unknown>,
  ) =>
    call<{ recorded: true }>(`/api/creative-plans/${creativePlanId}/signals`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, signal, metadata }),
    }),
  correctProductIntelligence: (
    workspaceId: string,
    productProjectId: string,
    factKey: string,
    value: string,
  ) =>
    call<{ fact: { id: string; value: string; kind: string; confidence: number } }>(
      `/api/projects/${productProjectId}/intelligence`,
      { method: 'PATCH', body: JSON.stringify({ workspaceId, factKey, value }) },
    ),
  creativePlanRevisions: (workspaceId: string, creativePlanId: string) =>
    call<{ revisions: CreativePlanRevision[] }>(
      `/api/creative-plans/${creativePlanId}/revisions?${qs({ workspaceId })}`,
    ),
  restoreCreativePlanRevision: (workspaceId: string, creativePlanId: string, revisionId: string) =>
    call<{
      planId: string;
      revisionId: string;
      concept: CreativePlan['concept'];
      creativeSpec: CreativePlan['creativeSpec'];
    }>(`/api/creative-plans/${creativePlanId}/revisions/${revisionId}/restore`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    }),
  creativeReferences: (workspaceId: string, projectId: string) =>
    call<{ references: CreativeReference[] }>(
      `/api/projects/${projectId}/references?${qs({ workspaceId })}`,
    ),
  addCreativeReference: async (input: {
    workspaceId: string;
    projectId: string;
    referenceType: 'design' | 'competitor';
    semanticRole: CreativeReference['semanticRole'];
    annotation: string;
    file?: File;
    assetId?: string;
  }): Promise<{ reference: CreativeReference }> => {
    const form = new FormData();
    form.set('workspaceId', input.workspaceId);
    form.set('referenceType', input.referenceType);
    form.set('semanticRole', input.semanticRole);
    form.set('annotation', input.annotation);
    if (input.file) form.set('file', input.file);
    if (input.assetId) form.set('assetId', input.assetId);
    const response = await fetch(`/api/projects/${input.projectId}/references`, {
      method: 'POST',
      body: form,
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      throw new ApiError(body?.error?.code ?? 'INTERNAL_ERROR', response.status);
    }
    return (await response.json()) as { reference: CreativeReference };
  },
  removeCreativeReference: (workspaceId: string, projectId: string, referenceId: string) =>
    call<{ deleted: boolean }>(
      `/api/projects/${projectId}/references/${referenceId}?${qs({ workspaceId })}`,
      { method: 'DELETE' },
    ),
  generatePlanBatch: (
    key: string,
    input: {
      workspaceId: string;
      creativePlanIds: string[];
      modelProfileId: string;
      quantityPerPlan: number;
      expectedTotalCredits: number;
      /** Overrides the ratio the board was developed at. */
      ratio?: string;
      /** Reaches the image model directly, unlike the board's direction box. */
      renderDirection?: string;
      /** A look per concept, when Krevya picked them itself. Keyed by plan id. */
      renderDirectionByPlan?: Record<string, string>;
      /** Draw the picture and no words at all. */
      noText?: boolean;
    },
  ) =>
    call<{ batchId: string; jobIds: string[]; totalCredits: number; replayed: boolean }>(
      '/api/generations/batch',
      { method: 'POST', headers: { 'idempotency-key': key }, body: JSON.stringify(input) },
    ),
  quotePlanBatch: (input: {
    workspaceId: string;
    creativePlanIds: string[];
    modelProfileId: string;
    quantityPerPlan: number;
  }) =>
    call<{
      quote: {
        planCount: number;
        quantityPerPlan: number;
        totalImages: number;
        creditsPerImage: number;
        totalCredits: number;
        availableCredits: number;
        canAfford: boolean;
        model: { id: string; name: string };
      };
    }>('/api/generations/batch/quote', { method: 'POST', body: JSON.stringify(input) }),
  balance: (workspaceId: string) => call<Balance>(`/api/credits/balance?${qs({ workspaceId })}`),

  ledger: (workspaceId: string, page?: PageQuery) =>
    call<{ entries: LedgerEntry[]; total: number; nextCursor: string | null }>(
      `/api/credits/ledger?${qs({ workspaceId, ...pageQs(page) })}`,
    ),

  isAdmin: () => call<{ isAdmin: boolean }>('/api/admin/me'),

  /** Where this browser stands with the admin area: enrol, verify, or in. */
  adminSession: () => call<AdminSessionState>('/api/admin/session'),
  /**
   * Confirms the authenticator that was just scanned and opens the first admin
   * session in the same request, so the code is spent once. The browser keeps
   * the cookies it sets.
   */
  adminEnrollConfirm: (code: string) =>
    call<AdminVerified>('/api/admin/mfa/enroll/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  /** Opens an admin session from an authenticator code. */
  adminVerify: (code: string) =>
    call<AdminVerified>('/api/admin/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  /** The same, from a recovery code. */
  adminRecovery: (code: string) =>
    call<AdminVerified>('/api/admin/recovery', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  /** Proves it is still the operator before one sensitive action. */
  adminStepUp: (code: string) =>
    call<{ mfaVerifiedAt: number; recoveryCodesRemaining: number | null }>('/api/admin/step-up', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  /** A new set of recovery codes, shown once. Password, a fresh code and a reason are required. */
  adminRecoveryRegenerate: (input: { reason: string; password: string; code: string }) =>
    call<{ recoveryCodes: string[] }>('/api/admin/mfa/recovery/regenerate', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Turns the second factor off and ends every admin session. */
  adminDisableMfa: (input: { reason: string; password: string; code: string }) =>
    call<{ status: 'disabled' }>('/api/admin/mfa/disable', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  adminSessions: () => call<{ sessions: AdminSessionRow[] }>('/api/admin/sessions'),
  adminRevokeOtherSessions: (reason: string) =>
    call<{ revoked: number }>('/api/admin/sessions/revoke-others', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  adminRevokeAllSessions: (reason: string) =>
    call<{ revoked: number }>('/api/admin/sessions/revoke-all', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  adminLogout: () =>
    call<{ status: string }>('/api/admin/logout', { method: 'POST', body: JSON.stringify({}) }),

  adminWorkspaces: (search: string) =>
    call<{ workspaces: AdminWorkspace[] }>(`/api/admin/workspaces?${qs({ search })}`),

  adminLedger: (workspaceId: string, page?: PageQuery) =>
    call<{ balance: Balance; entries: LedgerEntry[]; total: number; nextCursor: string | null }>(
      `/api/admin/credits/ledger?${qs({ workspaceId, ...pageQs(page) })}`,
    ),

  adminGrant: (input: {
    workspaceId: string;
    amount: number;
    reason: string;
    operationId: string;
  }) =>
    call<{ outcome: string; balance: Balance }>('/api/admin/credits/grant', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  adminAdjust: (input: {
    workspaceId: string;
    delta: number;
    reason: string;
    operationId: string;
  }) =>
    call<{ outcome: string; balance: Balance }>('/api/admin/credits/adjust', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  adminProviders: () => call<{ providers: ProviderAvailability[] }>('/api/admin/providers'),
  adminOperationsSummary: () => call<OperationsSummary>('/api/admin/operations/summary'),
  /**
   * Left without a currency, the API prices in the visitor's remembered choice
   * or their country's currency; a subscribed workspace is always answered in
   * its plan's currency, with `currencyLocked` set.
   */
  billingPlans: (workspaceId: string, currency?: string) =>
    call<{
      configured: boolean;
      plans: PlanOffer[];
      subscription: Subscription | null;
      /** Absent from older answers, which were dollars. */
      currency?: string;
      currencyLocked?: boolean;
      availableCurrencies?: string[];
    }>(`/api/billing/plans?${qs({ workspaceId, currency })}`),
  /** Signed-out and cached: the prices the landing page shows. */
  publicPricing: (currency?: string) => {
    // Without a currency the address stays bare, so the server picks one
    // from the visitor's country and cookie.
    const path = '/api/billing/public-plans';
    return call<{ plans: PublicPlan[]; currency?: string; availableCurrencies?: string[] }>(
      currency ? `${path}?${qs({ currency })}` : path,
    );
  },
  billingInvoices: (workspaceId: string, page?: PageQuery) =>
    call<{ invoices: Invoice[]; nextCursor: string | null; total: number }>(
      `/api/billing/invoices?${qs({ workspaceId, ...pageQs(page) })}`,
    ),
  /**
   * `promoApplied` true means the code is live, not that it is applied: Whop's
   * checkout cannot be opened with a code already in it, so the merchant has to
   * type it there. False means the code was sent and is no longer live; the
   * checkout still opens, at full price.
   */
  billingCheckout: (
    workspaceId: string,
    planId: string,
    promoCode?: string,
    /** Left out for a month, which is what the API assumes. */
    interval?: 'month' | 'year',
    /** The currency the prices were shown in; a subscribed workspace is held to its own. */
    currency?: string,
  ) =>
    call<{ url: string; checkoutId: string; promoApplied: boolean }>('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({
        workspaceId,
        planId,
        ...(promoCode ? { promoCode } : {}),
        ...(interval === 'year' ? { interval } : {}),
        ...(currency ? { currency } : {}),
      }),
    }),
  billingPortal: (workspaceId: string) =>
    call<{ url: string }>('/api/billing/portal', {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    }),
  /** The one-time Krevya Theme licence: its price, and whether this workspace needs it. */
  themeLicense: (workspaceId: string) =>
    call<{
      currency: Currency;
      priceMinor: number;
      owned: boolean;
      coveredBySubscription: boolean;
    }>(`/api/billing/theme-license?${qs({ workspaceId })}`),
  themeLicenseCheckout: (workspaceId: string, currency?: string) =>
    call<{ url: string; checkoutId: string }>('/api/billing/theme-license/checkout', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, ...(currency ? { currency } : {}) }),
    }),
  adminOverview: (window: string) => call<AdminOverview>(`/api/admin/overview?${qs({ window })}`),
  adminCustomers: (query?: string, page?: PageQuery) =>
    call<{ customers: AdminCustomer[]; total: number; nextCursor: string | null }>(
      `/api/admin/customers?${qs({ query, ...pageQs(page) })}`,
    ),
  adminCustomer: (workspaceId: string) =>
    call<AdminCustomerDetail>(`/api/admin/customers/${workspaceId}`),
  adminActivity: (page?: PageQuery) =>
    call<AdminActivity>(`/api/admin/activity?${qs({ ...pageQs(page) })}`),
  /** Page two and beyond of one account's generations, boards, ledger or invoices. */
  adminCustomerList: <T>(workspaceId: string, list: string, page?: PageQuery) =>
    call<{ items: T[]; nextCursor: string | null; total: number }>(
      `/api/admin/customers/${workspaceId}/${list}?${qs({ ...pageQs(page) })}`,
    ),
  adminUnknownItems: () =>
    call<{
      items: {
        id: string;
        jobId: string;
        provider: string;
        providerModel: string;
        reservedCredits: number;
        startedAt: number | null;
      }[];
    }>('/api/admin/unknown-items'),
  adminStrandedItems: () =>
    call<{
      items: StrandedItem[];
      total: number;
      totalCreditsHeld: number;
      /** False where there is no bucket to ask, so the storage columns mean "not checked". */
      storageChecked: boolean;
    }>('/api/admin/stranded-items'),
  /**
   * `operationId` is the caller's id for this release. A retry of a request
   * that may already have reached the server must reuse it: the server answers
   * the repeat with what the first attempt returned rather than moving the
   * credits a second time.
   */
  releaseStrandedItem: (itemId: string, reason: string, operationId: string) =>
    call<{ outcome: string; creditsReturned: number }>(
      `/api/admin/stranded-items/${itemId}/release`,
      { method: 'POST', body: JSON.stringify({ reason, operationId }) },
    ),
  adminEvaluations: () =>
    call<{
      items: {
        itemId: string;
        assetId: string;
        workspaceId: string;
        overallScore: number | null;
        productFidelityScore: number | null;
        promptAdherenceScore: number | null;
        textQualityScore: number | null;
        visualQualityScore: number | null;
        accepted: number | null;
        notes: string | null;
      }[];
    }>('/api/admin/evaluations'),
  saveEvaluation: (
    itemId: string,
    input: {
      overallScore: number;
      productFidelityScore: number;
      promptAdherenceScore: number;
      textQualityScore: number;
      visualQualityScore: number;
      accepted: boolean;
      notes: string;
    },
  ) =>
    call<{ status: string }>(`/api/admin/generation-items/${itemId}/evaluation`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  adminEvalRuns: (page?: PageQuery) =>
    call<{ runs: EvalRun[]; nextCursor: string | null; total: number }>(
      `/api/admin/eval-runs?${qs({ ...pageQs(page) })}`,
    ),
  adminEvalRun: (id: string) =>
    call<{ run: EvalRun; items: EvalRunItem[] }>(`/api/admin/eval-runs/${id}`),
  /**
   * Starts an evaluation run, which spends the chosen workspace's credits, one
   * per case. `operationId` is the caller's id for this run: a retry reuses it
   * and gets the run the first attempt started, jobs and all.
   */
  createEvalRun: (input: {
    workspaceId: string;
    modelProfileId: string;
    productProjectId: string;
    creativeSetId: string;
    ratio: string;
    language: string;
    evalCaseIds: string[];
    operationId: string;
    /** Why the run is being started; the server refuses a run without one. */
    reason: string;
    brandColor?: string;
  }) =>
    call<{ runId: string; jobs: string[]; creditPolicy: string }>('/api/admin/eval-runs', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  evalComparison: (ids: string[]) =>
    call<{ comparable: boolean; runs: EvalComparison[]; noRealBenchmarkData: string[] }>(
      `/api/admin/eval-comparison?${qs({ runIds: ids.join(',') })}`,
    ),

  me: () => call<{ user: { id: string; email: string }; workspaces: Workspace[] }>('/api/me'),

  /**
   * Starts a generation.
   *
   * The idempotency key is the caller's, and an ambiguous retry must reuse it
   * a new key would create a second job and reserve credits twice.
   */
  generate: (
    idempotencyKey: string,
    input: {
      workspaceId: string;
      productProjectId: string;
      creativeSetId: string;
      modelProfileId: string;
      ratio: string;
      quantity: number;
      language: string;
      brandColor: string;
      customDirection: string;
      expectedTotalCredits: number;
      /** True when this look is written to carry no words. */
      noText?: boolean;
    },
  ) =>
    call<{ jobId: string; status: string; reservedCredits: number }>('/api/generations', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(input),
    }),

  /** The same order at several ratios, priced as one total. */
  adSetQuote: (input: {
    workspaceId: string;
    productProjectId: string;
    creativeSetId: string;
    modelProfileId: string;
    ratios: string[];
    quantity: number;
    language: string;
    brandColor: string;
    customDirection: string;
    variants?: AdSetVariantInput[];
  }) =>
    call<{ quote: AdSetQuote }>('/api/generations/ad-set/quote', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** One job per ratio under one submission key, so a retry cannot reserve twice. */
  generateAdSet: (
    idempotencyKey: string,
    input: {
      workspaceId: string;
      productProjectId: string;
      creativeSetId: string;
      modelProfileId: string;
      ratios: string[];
      quantity: number;
      language: string;
      brandColor: string;
      customDirection: string;
      variants?: AdSetVariantInput[];
      expectedTotalCredits: number;
    },
  ) =>
    call<{ jobIds: string[]; reservedCredits: number; replayed: boolean }>(
      '/api/generations/ad-set',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(input),
      },
    ),

  generation: (workspaceId: string, jobId: string) =>
    call<{
      generation: Generation;
      batchJobIds: string[];
      /** The ad format each job of a set was made in, by job id. */
      batchFormats?: Record<string, string | null>;
    }>(`/api/generations/${jobId}?${qs({ workspaceId })}`),
  /** Run a job's failed items again, on the concept it already has. */
  retryGeneration: (workspaceId: string, jobId: string) =>
    call<{ jobId: string; itemCount: number; reservedCredits: number }>(
      `/api/generations/${jobId}/retry`,
      { method: 'POST', body: JSON.stringify({ workspaceId }) },
    ),
  generations: (
    workspaceId: string,
    projectId?: string,
    page?: PageQuery,
    /** Defaults to images server side, as every caller before video did. */
    media?: 'image' | 'video',
  ) =>
    call<{ generations: Generation[]; nextCursor: string | null }>(
      `/api/generations?${qs({ workspaceId, projectId, media, ...pageQs(page) })}`,
    ),

  /** Jobs still in flight, for the dock that reports work as it lands. */
  activeGenerations: (workspaceId: string) =>
    call<{ generations: Generation[]; nextCursor: string | null }>(
      `/api/generations?${qs({ workspaceId, active: 'true', limit: '10' })}`,
    ),

  library: (workspaceId: string, page?: PageQuery, filters?: LibraryFilters) =>
    call<{ assets: LibraryAsset[]; nextCursor: string | null }>(
      `/api/library?${qs({
        workspaceId,
        search: filters?.search,
        ratio: filters?.ratio,
        projectId: filters?.projectId,
        media: filters?.media,
        tag: filters?.tag,
        ...pageQs(page),
      })}`,
    ),

  suggestCopy: (workspaceId: string, productProjectId: string, instruction?: string) =>
    call<{ suggestions: CopySuggestion[]; grounded: boolean }>('/api/creative-copy/suggest', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, productProjectId, instruction, count: 3 }),
    }),

  /** Replaces a creative's labels; the server answers with the cleaned list. */
  setAssetTags: (workspaceId: string, assetId: string, tags: string[]) =>
    call<{ tags: string[] }>(`/api/assets/${assetId}/tags`, {
      method: 'PUT',
      body: JSON.stringify({ workspaceId, tags }),
    }),
  /** The labels in use, most used first. */
  assetTags: (workspaceId: string) =>
    call<{ tags: { tag: string; count: number }[] }>(`/api/asset-tags?${qs({ workspaceId })}`),

  /** Included in the plan and counted against the daily allowance, like `suggestCopy`. */
  platformCopy: (input: {
    workspaceId: string;
    productProjectId: string;
    language?: string;
    format?: string;
    angle?: string;
  }) =>
    call<{ copy: PlatformAdCopy; grounded: boolean }>('/api/creative-copy/platform', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  assetProvenance: (workspaceId: string, assetId: string) =>
    call<AssetProvenance>(`/api/assets/${assetId}/provenance?${qs({ workspaceId })}`),

  deleteAsset: (workspaceId: string, assetId: string) =>
    call<{ deleted: boolean }>(`/api/assets/${assetId}?${qs({ workspaceId })}`, {
      method: 'DELETE',
    }),

  deleteAssets: (workspaceId: string, assetIds: string[]) =>
    call<{ deleted: number }>('/api/assets/delete', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, assetIds }),
    }),

  /**
   * Another go at the concept a finished picture came from.
   *
   * Three things at once, because they are the same request: one more of the
   * same, several more like this one, and this one drawn differently. What
   * changes is the quantity and whether a direction is attached, the concept
   * is never rewritten, because the merchant is looking at a picture they
   * already partly like.
   */
  regenerateAsset: (
    idempotencyKey: string,
    input: {
      workspaceId: string;
      assetId: string;
      expectedTotalCredits: number;
      quantityPerPlan?: number;
      renderDirection?: string;
      ratio?: string;
      modelProfileId?: string;
      noText?: boolean;
      /**
       * Change this picture instead of drawing its concept again.
       *
       * The finished image goes to the model as its first reference and the
       * direction becomes the instruction, so what the merchant did not ask
       * about survives. Requires a direction, and refuses a ratio: a new
       * canvas has nothing to preserve.
       */
      edit?: boolean;
    },
  ) =>
    call<{ jobIds: string[]; replayed: boolean }>(`/api/assets/${input.assetId}/regenerate`, {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify({
        workspaceId: input.workspaceId,
        expectedTotalCredits: input.expectedTotalCredits,
        ...(input.quantityPerPlan ? { quantityPerPlan: input.quantityPerPlan } : {}),
        ...(input.renderDirection ? { renderDirection: input.renderDirection } : {}),
        ...(input.edit ? { edit: true } : {}),
        ...(input.ratio ? { ratio: input.ratio } : {}),
        ...(input.modelProfileId ? { modelProfileId: input.modelProfileId } : {}),
        ...(input.noText ? { noText: true } : {}),
      }),
    }),

  /** What has been made but not yet judged, oldest first. */
  triageDeck: (workspaceId: string, projectId?: string | null, limit = 20) =>
    call<{ cards: TriageCard[]; remaining: number }>(
      `/api/triage?${qs({ workspaceId, projectId: projectId ?? undefined, limit: String(limit) })}`,
    ),

  /** Keep it or pass on it. Nothing here spends credits. */
  triage: (workspaceId: string, assetId: string, verdict: 'keep' | 'pass') =>
    call<{ recorded: boolean; signal: string }>(`/api/triage/${assetId}`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, verdict }),
    }),

  assetUrl: (workspaceId: string, assetId: string) =>
    `/api/assets/${assetId}?${qs({ workspaceId })}`,

  /**
   * The in-progress frame for an item.
   *
   * `at` busts the browser cache: the same URL returns a further-along frame
   * each time, and without it the first one would be shown for the whole run.
   */
  itemPreviewUrl: (workspaceId: string, itemId: string, at: number) =>
    `/api/generation-items/${itemId}/preview?${qs({ workspaceId, t: String(at) })}`,

  feedback: (workspaceId: string, assetId: string, value: 1 | -1, reason?: string) =>
    call<{ status: string }>(`/api/assets/${assetId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, value, ...(reason ? { reason } : {}) }),
    }),

  /**
   * One pasted address instead of an OAuth screen.
   *
   * Returns immediately with an id; the reading happens on the queue and the
   * screen follows it with `urlImport`.
   */
  startUrlImport: (workspaceId: string, url: string) =>
    call<{ importId: string; reused: boolean }>('/api/onboarding/from-url', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, url }),
    }),

  urlImport: (workspaceId: string, importId: string) =>
    call<StoreImport>(`/api/onboarding/from-url/${importId}?${qs({ workspaceId })}`),

  /** The setup screen resumes from this, never from what a closed tab remembered. */
  onboardingState: (workspaceId: string) =>
    call<OnboardingState>(`/api/onboarding/state?${qs({ workspaceId })}`),

  /** A step the merchant finished. Idempotent: the first moment is kept. */
  markOnboardingStep: (
    workspaceId: string,
    step: 'store' | 'catalog' | 'first_create' | 'dismiss',
  ) =>
    call<OnboardingState>('/api/onboarding/progress', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, step }),
    }),

  /**
   * "Is this your brand?" answered. Confirming writes one kit however often it
   * is sent; `importId` confirms an address import's read instead.
   */
  decideOnboardingBrand: (
    workspaceId: string,
    input: {
      decision: 'confirmed' | 'skipped';
      brand?: Partial<ImportedBrand> & { palette?: string[] };
      importId?: string;
    },
  ) =>
    call<{ brandKitId: string | null; decision: string; state: OnboardingState }>(
      '/api/onboarding/brand',
      { method: 'POST', body: JSON.stringify({ workspaceId, ...input }) },
    ),

  /** A brand proposed from the connected Shopify store. Read-only. */
  proposeStoreBrand: (workspaceId: string) =>
    call<StoreBrandProposal>('/api/onboarding/store-brand', {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    }),

  confirmImportedBrand: (workspaceId: string, importId: string, brand: Partial<ImportedBrand>) =>
    call<{ brandKitId: string }>(`/api/onboarding/from-url/${importId}/brand`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, brand }),
    }),

  /** The handful worth starting with, ranked by the best signal the shop has. */
  suggestedProducts: (workspaceId: string, limit = 6) =>
    call<SuggestedProducts>(`/api/products/suggested?${qs({ workspaceId, limit: String(limit) })}`),

  /** What Krevya currently believes about the store. Null until it has read one. */
  storeProfile: (workspaceId: string, productProjectId?: string) =>
    // `productFits` says whether the shop's buyers are this product's buyers;
    // null when no product was asked about or the catalogue cannot say.
    call<{ profile: StoreProfile | null; productFits?: boolean | null }>(
      `/api/store-profile?${qs({ workspaceId, ...(productProjectId ? { productProjectId } : {}) })}`,
    ),

  /**
   * Reads the catalogue again.
   *
   * Runs on its own after a sync; this is the button for a merchant who has
   * just changed what they sell and does not want to wait. A profile they
   * already corrected keeps their words.
   */
  storeProfileInfer: (workspaceId: string) =>
    call<{ profile: StoreProfile | null; status: 'ok' | 'no_products' }>(
      '/api/store-profile/infer',
      { method: 'POST', body: JSON.stringify({ workspaceId }) },
    ),

  /** The merchant's answer. Sending nothing but the workspace means "this is right". */
  storeProfileConfirm: (
    workspaceId: string,
    input: { category?: string; subcategory?: string | null; audiences?: StoreAudience[] } = {},
  ) =>
    call<{ profile: StoreProfile }>('/api/store-profile/confirm', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, ...input }),
    }),

  /** A complete storefront writing pass. Images remain a separate, quoted operation. */
  createStorePack: (input: {
    workspaceId: string;
    productProjectId: string;
    audience?: {
      awareness?: number;
      hardness?: number;
      gender?: 'female' | 'male' | 'mixed' | null;
      ageFrom?: number | null;
      ageTo?: number | null;
    };
    market?: string | null;
    language?: string;
    extraDirection?: string | null;
    /** True: reuse saved research or pay for a fresh run. False: the opt-out. */
    research?: boolean;
    competitorUrls?: string[];
    /** The total the button showed. A charge that differs from it is refused. */
    confirmedTotal?: number;
  }) =>
    call<StorePackSnapshot>('/api/store-packs', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** The one total in front of a store pack. Nothing is reserved by asking. */
  storePackQuote: (input: {
    workspaceId: string;
    productProjectId: string;
    research: boolean;
    market?: string | null;
    language?: string;
  }) =>
    call<StorePackQuote>('/api/store-packs/quote', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  storePacks: (workspaceId: string, productProjectId: string) =>
    call<{ packs: StorePackSummary[] }>(
      `/api/store-packs?${qs({ workspaceId, productProjectId })}`,
    ),

  storePack: (id: string) => call<StorePackSnapshot>(`/api/store-packs/${id}`),

  /** Whether the published storefront claims these reviews are from verified buyers. */
  setStorePackReviewsVerified: (id: string, workspaceId: string, reviewsVerified: boolean) =>
    call<StorePackSnapshot>(`/api/store-packs/${id}/reviews-verified`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, reviewsVerified }),
    }),

  /** What the merchant has changed in the theme editor, as Krevya holds it. */
  storePackThemeEdits: (id: string, workspaceId: string) =>
    call<{ edits: StorePackThemeEdits; revision?: string }>(
      `/api/store-packs/${id}/theme-edits?${qs({ workspaceId })}`,
    ),
  /** Words in, edits out: nothing is saved by this, it only proposes. */
  assistStorePackThemeEdits: (id: string, body: Record<string, unknown>) =>
    call<{
      summary: string;
      steps: ThemeAssistStep[];
      imageSuggestions?: ThemeAssistImageSuggestion[];
      /** What the answer was written from: real research, the listing alone,
       *  or nothing but the page. Said to the merchant, never hidden. */
      grounding?: {
        level: 'research' | 'listing' | 'page_only';
        sources: string[];
        missing: string[];
      };
    }>(`/api/store-packs/${id}/theme-edits/assist`, { method: 'POST', body: JSON.stringify(body) }),

  /** `baseRevision` is the version these edits were made on top of; a save made
   *  over somebody else's newer one is refused with `CONFLICT`. */
  saveStorePackThemeEdits: (
    id: string,
    workspaceId: string,
    edits: StorePackThemeEdits,
    baseRevision?: string,
  ) =>
    call<{ edits: StorePackThemeEdits; revision?: string }>(`/api/store-packs/${id}/theme-edits`, {
      method: 'PUT',
      body: JSON.stringify({ workspaceId, edits, ...(baseRevision ? { baseRevision } : {}) }),
    }),

  storePackImages: (id: string, workspaceId: string) =>
    call<StorePackImages>(`/api/store-packs/${id}/images?${qs({ workspaceId })}`),
  selectStorePackImage: (id: string, slotKey: string, workspaceId: string, assetId: string) =>
    call<StorePackImages>(`/api/store-packs/${id}/images/${encodeURIComponent(slotKey)}/select`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, assetId }),
    }),
  skipStorePackImage: (id: string, slotKey: string, workspaceId: string) =>
    call<StorePackImages>(`/api/store-packs/${id}/images/${encodeURIComponent(slotKey)}/skip`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    }),
  quoteStorePackImages: (
    id: string,
    input: { workspaceId: string; modelProfileId: string; slotKeys: string[] },
  ) =>
    call<StorePackImageQuote>(`/api/store-packs/${id}/images/quote`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  generateStorePackImages: (
    id: string,
    input: {
      workspaceId: string;
      modelProfileId: string;
      slotKeys: string[];
      expectedTotalCredits: number;
      revision: number;
      copyHash: string;
    },
    idempotencyKey: string,
  ) =>
    call<StorePackImages>(`/api/store-packs/${id}/images/generate`, {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(input),
    }),

  regenerateStorePackPart: (id: string, kind: StorePackPart['kind']) =>
    call<StorePackSnapshot>(`/api/store-packs/${id}/parts/${kind}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  stageKrevyaTheme: (input: {
    workspaceId: string;
    productProjectId: string;
    storePackId: string;
  }) =>
    call<{ deployment: ThemeDeployment }>('/api/theme-deployments', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  themeDeployment: (id: string) =>
    call<{ deployment: ThemeDeployment }>(`/api/theme-deployments/${id}`),

  /** The draft's own theme JSON, read from Shopify. Never writes. */
  themeDeploymentFiles: (id: string) =>
    call<{
      files: {
        index: string | null;
        product: string | null;
        headerGroup: string | null;
        footerGroup: string | null;
        settingsData: string | null;
        images: Record<string, string>;
      };
    }>(`/api/theme-deployments/${id}/theme-files`),

  themeDeployments: (workspaceId: string, storePackId: string) =>
    call<{ deployments: ThemeDeployment[] }>(
      `/api/theme-deployments?${qs({ workspaceId, storePackId })}`,
    ),

  retryThemeDeployment: (id: string) =>
    call<{ deployment: ThemeDeployment }>(`/api/theme-deployments/${id}/retry`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  publishThemeDeployment: (id: string) =>
    call<{ deployment: ThemeDeployment }>(`/api/theme-deployments/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  rollbackThemeDeployment: (id: string) =>
    call<{ deployment: ThemeDeployment }>(`/api/theme-deployments/${id}/rollback`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  integrations: (workspaceId: string) =>
    call<{
      integrations: Integration[];
      /** Stores the plan may have connected at once; null is no limit. */
      storeLimit?: number | null;
      /** Shopify stores connected now, counted the way the limit counts them. */
      storesUsed?: number;
    }>(`/api/integrations?${qs({ workspaceId })}`),

  storefrontConnection: (workspaceId: string, integrationId: string) =>
    call<StorefrontConnection>(
      `/api/integrations/${integrationId}/storefront?${qs({ workspaceId })}`,
    ),
  storefrontSignups: (
    workspaceId: string,
    integrationId: string,
    filter: StorefrontSignupFilter = {},
    limit = 50,
  ) =>
    call<{ signups: StorefrontSignup[]; nextBefore: number | null }>(
      `/api/integrations/${integrationId}/signups?${qs({
        workspaceId,
        limit: String(limit),
        ...(filter.status ? { status: filter.status } : {}),
        ...(filter.source ? { source: filter.source } : {}),
        ...(filter.email ? { email: filter.email } : {}),
        ...(filter.before ? { before: String(filter.before) } : {}),
      })}`,
    ),
  storefrontSignupSummary: (workspaceId: string, integrationId: string) =>
    call<{ summary: StorefrontSignupSummary }>(
      `/api/integrations/${integrationId}/signups/summary?${qs({ workspaceId })}`,
    ),
  retryStorefrontSignup: (workspaceId: string, signupId: string) =>
    call<{ signup: StorefrontSignup; error: string | null }>(
      `/api/storefront-signups/${signupId}/retry`,
      { method: 'POST', body: JSON.stringify({ workspaceId }) },
    ),

  /* E-mail automations (Marketing). Shapes live in marketing/email-types.ts. */
  emailFlows: (workspaceId: string, integrationId: string) =>
    call<EmailFlowsOverview>(
      `/api/integrations/${integrationId}/email-flows?${qs({ workspaceId })}`,
    ),
  updateEmailFlow: (
    workspaceId: string,
    flowId: string,
    change: { enabled?: boolean; settings?: EmailFlowSettings },
  ) =>
    call<{ flow: EmailFlow }>(`/api/email-flows/${flowId}`, {
      method: 'PATCH',
      body: JSON.stringify({ workspaceId, ...change }),
    }),
  updateEmailFlowStep: (
    workspaceId: string,
    stepId: string,
    change: { enabled?: boolean; delayMinutes?: number },
  ) =>
    call<{ flow: EmailFlow }>(`/api/email-flow-steps/${stepId}`, {
      method: 'PATCH',
      body: JSON.stringify({ workspaceId, ...change }),
    }),
  /**
   * Moves a mail to another place in the chain. The schedule stays where it is
   * and the mails change places, so the one that lands in the first slot goes
   * as soon as the flow starts.
   */
  moveEmailFlowStep: (workspaceId: string, stepId: string, position: number) =>
    call<{ flow: EmailFlow }>(`/api/email-flow-steps/${stepId}`, {
      method: 'PATCH',
      body: JSON.stringify({ workspaceId, position }),
    }),
  /** Without a position the mail goes on the end; with one it opens the chain there. */
  addEmailFlowStep: (workspaceId: string, flowId: string, position?: number) =>
    call<{ flow: EmailFlow }>(`/api/email-flows/${flowId}/steps`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, position }),
    }),
  removeEmailFlowStep: (workspaceId: string, stepId: string) =>
    call<{ flow: EmailFlow }>(`/api/email-flow-steps/${stepId}?${qs({ workspaceId })}`, {
      method: 'DELETE',
    }),
  emailStepTemplate: (workspaceId: string, stepId: string, language: EmailLanguage) =>
    call<{ template: EmailStepTemplate; preview: EmailPreview }>(
      `/api/email-flow-steps/${stepId}/template?${qs({ workspaceId, language })}`,
    ),
  saveEmailStepTemplate: (
    workspaceId: string,
    stepId: string,
    language: EmailLanguage,
    content: EmailTemplateContent,
  ) =>
    call<{ template: EmailStepTemplate }>(`/api/email-flow-steps/${stepId}/template`, {
      method: 'PUT',
      body: JSON.stringify({ workspaceId, language, content }),
    }),
  resetEmailStepTemplate: (workspaceId: string, stepId: string, language: EmailLanguage) =>
    call<{ template: EmailStepTemplate }>(`/api/email-flow-steps/${stepId}/template/reset`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, language }),
    }),
  previewEmailStep: (
    workspaceId: string,
    stepId: string,
    language: EmailLanguage,
    content: EmailTemplateContent,
  ) =>
    call<{ preview: EmailPreview }>(`/api/email-flow-steps/${stepId}/preview`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, language, content }),
    }),
  testSendEmailStep: (
    workspaceId: string,
    stepId: string,
    language: EmailLanguage,
    content: EmailTemplateContent,
  ) =>
    call<EmailTestSendResult>(`/api/email-flow-steps/${stepId}/test-send`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, language, content }),
    }),
  writeEmailStep: (
    workspaceId: string,
    stepId: string,
    language: EmailLanguage,
    instruction: string,
  ) =>
    call<{ draft: EmailDraft; rehearsal: boolean }>(`/api/email-flow-steps/${stepId}/write`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, language, instruction }),
    }),
  setSendingDomain: (workspaceId: string, integrationId: string, domain: string) =>
    call<{ domain: SendingDomain }>(`/api/integrations/${integrationId}/sending-domain`, {
      method: 'PUT',
      body: JSON.stringify({ workspaceId, domain }),
    }),
  verifySendingDomain: (workspaceId: string, integrationId: string) =>
    call<{ domain: SendingDomain }>(`/api/integrations/${integrationId}/sending-domain/verify`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    }),

  /**
   * Starts an install with the merchant's own Shopify app.
   *
   * Krevya no longer owns one app that every store installs. An app the
   * merchant owns is a custom app on their own store, which is what makes
   * order data available without an App Store review, so the client id and
   * secret come from the merchant and travel with the request.
   */
  connect: (
    workspaceId: string,
    shop: string,
    app: { clientId: string; clientSecret: string; credentialSource?: 'manual' | 'guided' },
  ) =>
    call<{ authorizeUrl: string }>('/api/integrations/shopify/connect', {
      method: 'POST',
      body: JSON.stringify({
        workspaceId,
        shop,
        clientId: app.clientId,
        clientSecret: app.clientSecret,
        ...(app.credentialSource ? { credentialSource: app.credentialSource } : {}),
      }),
    }),

  /**
   * Krevya creates the app itself.
   *
   * Signs the merchant in to Shopify once, creates the app in their own
   * organization and installs it, the manual guide's whole sequence without
   * any of the copying. Returns the sign-in URL to send the browser to.
   */
  shopifyProvisionStart: (workspaceId: string) =>
    call<{
      state: string;
      userCode: string;
      verificationUri: string;
      interval: number;
      expiresIn: number;
    }>('/api/integrations/shopify/provision/start', {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    }),

  /** The store the merchant picked; the app is created in its organization. */
  shopifyProvisionChoose: (
    workspaceId: string,
    input: { state: string; shop: string; organizationId: string },
  ) =>
    call<{ status: 'ready' | 'failed'; authorizeUrl?: string }>(
      '/api/integrations/shopify/provision/choose',
      { method: 'POST', body: JSON.stringify({ workspaceId, ...input }) },
    ),

  /**
   * Asks once whether the merchant has approved the code yet.
   *
   * One call per poll rather than a request that waits: approving in another
   * tab takes as long as a person takes, and the waiting belongs here rather
   * than in a Worker that would time out first.
   */
  shopifyProvisionPoll: (workspaceId: string, state: string) =>
    call<{
      status:
        | 'pending'
        | 'slow_down'
        | 'denied'
        | 'expired'
        | 'choose'
        | 'failed'
        // Every silence told apart. "You have no stores" used to be said for
        // all of them, including the ones that were ours.
        | 'null_account'
        | 'organizations_empty'
        | 'organizations_failed'
        | 'stores_empty'
        | 'stores_partial_failure'
        | 'stores_failed';
      stores?: ShopifyStoreChoice[];
    }>('/api/integrations/shopify/provision/poll', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, state }),
    }),

  /**
   * Exactly what to put into the merchant's own Shopify app.
   *
   * The scopes come from the server because it is the only thing that knows
   * what Krevya will actually ask for, and a setup guide that has drifted from
   * the real request is worse than no guide.
   */
  shopifySetupGuide: (workspaceId: string, shop?: string) =>
    call<{
      guide: {
        appName: string;
        appUrl: string;
        redirectUrl: string;
        scopes: string[];
        legacyInstallFlow: boolean;
        developAppsUrl: string | null;
      };
    }>(`/api/integrations/shopify/setup-guide?${qs({ workspaceId, shop })}`),

  sync: (workspaceId: string, integrationId: string) =>
    call<{ sync: SyncResult }>(`/api/integrations/${integrationId}/sync`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    }),

  disconnect: (workspaceId: string, integrationId: string) =>
    call<{ status: string }>(`/api/integrations/${integrationId}?${qs({ workspaceId })}`, {
      method: 'DELETE',
    }),

  /**
   * Offset paging, not a cursor: this list is ordered by title and reports a
   * total, so "page 3 of 40" is meaningful here in a way it is not for the
   * newest-first feeds.
   */
  products: (
    workspaceId: string,
    search?: string,
    page?: { limit?: number; offset?: number; status?: string; stage?: string; vendor?: string },
  ) =>
    call<{ products: Product[]; total: number; vendors: string[] }>(
      `/api/products?${qs({
        workspaceId,
        search,
        ...(page?.limit ? { limit: String(page.limit) } : {}),
        ...(page?.offset ? { offset: String(page.offset) } : {}),
        ...(page?.status ? { status: page.status } : {}),
        ...(page?.stage ? { stage: page.stage } : {}),
        ...(page?.vendor ? { vendor: page.vendor } : {}),
      })}`,
    ),

  product: (workspaceId: string, productId: string) =>
    call<{ product: Product & { description: string | null; images: { sourceUrl: string }[] } }>(
      `/api/products/${productId}?${qs({ workspaceId })}`,
    ),

  projects: (workspaceId: string, productId?: string) =>
    call<{ projects: Project[] }>(`/api/projects?${qs({ workspaceId, productId })}`),

  project: (workspaceId: string, projectId: string) =>
    call<{ project: Project }>(`/api/projects/${projectId}?${qs({ workspaceId })}`),

  createProject: (workspaceId: string, productId: string, name?: string) =>
    call<{ project: Project }>('/api/projects', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, productId, name }),
    }),

  creativeSets: () => call<{ creativeSets: CreativeSet[] }>('/api/creative-sets'),

  modelProfiles: () =>
    call<{ modelProfiles: ModelProfile[]; recommendedSlug: string; hasRealProvider: boolean }>(
      '/api/model-profiles',
    ),

  creativeOptions: () => call<CreativeOptions>('/api/creative-options'),

  /** The server is the authority on price; this response is what we display. */
  quote: (input: {
    workspaceId: string;
    productProjectId: string;
    creativeSetId: string;
    modelProfileId: string;
    ratio: string;
    quantity: number;
    language: string;
    brandColor: string;
    customDirection: string;
  }) =>
    call<{ quote: Quote }>('/api/generations/quote', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // -- Video (docs/video-generation-plan.md §4, §11 task 10) --------------

  videoCharacters: (workspaceId: string) =>
    call<{ videoCharacters: VideoCharacter[] }>(`/api/video-characters?${qs({ workspaceId })}`),

  createVideoCharacter: (
    workspaceId: string,
    input: {
      name: string;
      description?: string;
      kind: VideoCharacterKind;
      brandKitId?: string | null;
    },
  ) =>
    call<{ id: string; status: string }>('/api/video-characters', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, ...input }),
    }),

  /**
   * Uploads one frontal or angle reference image for a character.
   *
   * Multipart, mirroring `uploadBrandKitLogo`: raw `fetch`, not `call`, so the
   * browser sets the multipart boundary itself.
   */
  uploadVideoCharacterImage: async (
    workspaceId: string,
    characterId: string,
    file: File,
    role: VideoCharacterImageRole,
    position: number,
  ): Promise<{ imageId: string; assetId: string }> => {
    const form = new FormData();
    form.set('workspaceId', workspaceId);
    form.set('file', file);
    form.set('role', role);
    form.set('position', String(position));
    const response = await fetch(`/api/video-characters/${characterId}/images`, {
      method: 'POST',
      body: form,
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      throw new ApiError(body?.error?.code ?? 'INTERNAL_ERROR', response.status);
    }
    return (await response.json()) as { imageId: string; assetId: string };
  },

  removeVideoCharacterImage: (workspaceId: string, characterId: string, imageId: string) =>
    call<{ deleted: boolean }>(
      `/api/video-characters/${characterId}/images/${imageId}?${qs({ workspaceId })}`,
      { method: 'DELETE' },
    ),

  bindVideoCharacter: (workspaceId: string, characterId: string, provider: string) =>
    call<{ status: string }>(`/api/video-characters/${characterId}/bind`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, provider }),
    }),

  deleteVideoCharacter: (workspaceId: string, characterId: string) =>
    call<{ deleted: boolean }>(`/api/video-characters/${characterId}?${qs({ workspaceId })}`, {
      method: 'DELETE',
    }),

  /** `mode` defaults to 'create' server-side when omitted (docs/video-motion-edit-plan.md §8). */
  videoModels: (workspaceId: string, mode?: VideoMode) =>
    call<{ videoModels: VideoModel[] }>(`/api/video-models?${qs({ workspaceId, mode })}`),

  /**
   * Uploads a motion-source or edit-source video (docs/video-motion-edit-plan.md
   * §5, §11.12). Multipart, mirroring `uploadBrandKitLogo`. `onProgress` is
   * optional and, when given, is driven by `XMLHttpRequest` (the only way to
   * observe upload progress in a browser) instead of `fetch`.
   */
  uploadVideo: (
    workspaceId: string,
    purpose: 'motion' | 'source',
    file: File,
    onProgress?: (fraction: number) => void,
  ): Promise<{
    assetId: string;
    durationMs: number;
    width: number;
    height: number;
    mimeType: string;
  }> => {
    const form = new FormData();
    form.set('workspaceId', workspaceId);
    form.set('purpose', purpose);
    form.set('file', file);

    if (!onProgress) {
      return fetch('/api/video-uploads', { method: 'POST', body: form }).then(async (response) => {
        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as {
            error?: { code?: string };
          } | null;
          throw new ApiError(body?.error?.code ?? 'INTERNAL_ERROR', response.status);
        }
        return (await response.json()) as {
          assetId: string;
          durationMs: number;
          width: number;
          height: number;
          mimeType: string;
        };
      });
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/video-uploads');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      };
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          body = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(
            body as {
              assetId: string;
              durationMs: number;
              width: number;
              height: number;
              mimeType: string;
            },
          );
        } else {
          const code =
            (body as { error?: { code?: string } } | null)?.error?.code ?? 'INTERNAL_ERROR';
          reject(new ApiError(code, xhr.status));
        }
      };
      xhr.onerror = () => reject(new ApiError('INTERNAL_ERROR', 0));
      xhr.send(form);
    });
  },

  /** Cancels an upload before it is attached to a generation job. */
  deleteVideoUpload: (workspaceId: string, assetId: string) =>
    call<{ deleted: boolean }>(`/api/video-uploads/${assetId}?${qs({ workspaceId })}`, {
      method: 'DELETE',
    }),

  /** The server is the authority on price; this response is what we display. */
  quoteVideoGeneration: (input: VideoQuoteInput) =>
    call<{ quote: VideoQuote }>('/api/video-generations/quote', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Starts a video generation.
   *
   * Same idempotency contract as `generate`: the caller's key is reused on an
   * ambiguous retry rather than minted fresh, or credits would be reserved
   * twice for the same request.
   */
  createVideoGeneration: (
    idempotencyKey: string,
    input: VideoQuoteInput & { expectedTotalCredits: number },
  ) =>
    call<{ jobId: string; status: string; quantity: number; reservedCredits: number }>(
      '/api/video-generations',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(input),
      },
    ),

  /* ---------------------------------------------------------------------- */
  /* Support (docs/support/PLAN.md)                                          */
  /* ---------------------------------------------------------------------- */

  supportTickets: (workspaceId: string) =>
    call<{ tickets: SupportTicket[] }>(`/api/support/tickets?${qs({ workspaceId })}`),

  supportTicket: (workspaceId: string, ticketId: string) =>
    call<{ ticket: SupportTicket; messages: SupportMessage[] }>(
      `/api/support/tickets/${ticketId}?${qs({ workspaceId })}`,
    ),

  /**
   * Raises a ticket. Multipart because of the screenshots, so `call` is not
   * used: it forces a JSON content-type and a multipart body needs the browser
   * to write the header with its own boundary.
   */
  createSupportTicket: (
    workspaceId: string,
    input: { subject: string; category: SupportCategory; body: string; files: File[] },
  ) =>
    supportForm<{ ticket: SupportTicket }>('/api/support/tickets', workspaceId, input.files, {
      subject: input.subject,
      category: input.category,
      body: input.body,
    }),

  replySupportTicket: (
    workspaceId: string,
    ticketId: string,
    input: { body: string; files: File[] },
  ) =>
    supportForm<{ message: SupportMessage }>(
      `/api/support/tickets/${ticketId}/messages`,
      workspaceId,
      input.files,
      { body: input.body },
    ),

  /**
   * The attachment's address. Not a public object URL: this route checks
   * workspace membership before it streams a byte.
   */
  supportAttachmentUrl: (workspaceId: string, ticketId: string, attachmentId: string) =>
    `/api/support/tickets/${ticketId}/attachments/${attachmentId}?${qs({ workspaceId })}`,

  adminSupportTickets: (filters: { status?: SupportStatus; workspaceId?: string } = {}) =>
    call<{ tickets: StaffSupportTicket[] }>(
      `/api/admin/support/tickets?${qs({
        status: filters.status,
        workspaceId: filters.workspaceId,
      })}`,
    ),

  adminSupportTicket: (ticketId: string) =>
    call<{ ticket: StaffSupportTicket; messages: SupportMessage[] }>(
      `/api/admin/support/tickets/${ticketId}`,
    ),

  /**
   * A staff reply or note, with the same attachments a merchant may send.
   *
   * Multipart whether or not a file came with it, so there is one shape on the
   * wire and one code path on the server. `visibility` rides in the form, and
   * the server is what decides who ever sees the result.
   */
  adminReplySupportTicket: async (
    ticketId: string,
    input: { body: string; visibility: 'public' | 'internal'; files: File[] },
  ): Promise<{ message: SupportMessage; ticket: SupportTicket }> => {
    const form = new FormData();
    form.set('body', input.body);
    form.set('visibility', input.visibility);
    for (const file of input.files) form.append('file', file);
    const response = await fetch(`/api/admin/support/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: form,
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      throw new ApiError(body?.error?.code ?? 'INTERNAL_ERROR', response.status);
    }
    return (await response.json()) as { message: SupportMessage; ticket: SupportTicket };
  },

  /** `assign` is an intent, not an id: the server resolves "me" to the caller. */
  adminUpdateSupportTicket: (
    ticketId: string,
    input: {
      status?: SupportStatus;
      priority?: SupportPriority;
      assign?: 'me' | 'nobody';
    },
  ) =>
    call<{ ticket: SupportTicket }>(`/api/admin/support/tickets/${ticketId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  adminSupportAttachmentUrl: (ticketId: string, attachmentId: string) =>
    `/api/admin/support/tickets/${ticketId}/attachments/${attachmentId}`,
};

/** User-facing copy for the error taxonomy (ULTRAPLAN §94). */
export function messageForCode(code: string): string {
  const messages: Record<string, string> = {
    AUTH_REQUIRED: 'Please sign in again.',
    GENERATION_RETRY_LIMIT:
      'This one has been tried again as many times as it can be. Change the direction or the format and start a new one.',
    WORKSPACE_FORBIDDEN: 'You do not have access to this workspace.',
    SHOPIFY_NOT_CONNECTED: 'Connect a Shopify store first.',
    SHOPIFY_THEME_ACCESS_REQUIRED:
      'Reconnect Shopify with theme access before creating a storefront.',
    SHOPIFY_THEME_PROCESSING_FAILED:
      'Shopify could not process the Krevya Theme template. Retry the workflow.',
    SHOPIFY_DRAFT_ALREADY_LIVE:
      'This draft was published before its content was ready. Create a new draft to continue.',
    SHOPIFY_THEME_WORKFLOW_TIMEOUT:
      'Shopify did not finish preparing the draft in time. Start a new theme attempt.',
    SHOPIFY_PRODUCT_HANDLE_MISSING:
      'This Shopify product has no storefront handle. Sync the product and try again.',
    SHOPIFY_PRODUCT_TEMPLATE_INVALID:
      'This product uses an unsupported Shopify template name. Choose its default template and retry.',
    SHOPIFY_THEME_USER_ERROR:
      'Shopify rejected the theme operation. Check theme access and try again.',
    SHOPIFY_THEME_PLAN_UNAVAILABLE:
      'This Shopify plan does not currently allow apps to modify or publish themes.',
    SHOPIFY_MAIN_THEME_MISSING:
      'Shopify did not return the current live theme, so Krevya stopped before publishing.',
    SHOPIFY_LIVE_THEME_CHANGED:
      'Another theme is now live. Krevya left it unchanged; check your Shopify themes.',
    SHOPIFY_ROLLBACK_THEME_MISSING:
      'Krevya Theme is live, but Shopify did not return the previous theme needed for rollback.',
    STOREFRONT_CONTENT_INCOMPLETE:
      'Required storefront content is incomplete. Finish those sections and try again.',
    // Shared by every queued job — research, generation, store packs, themes —
    // so it names none of them. It used to say "theme workflow" to a merchant
    // whose product research had failed.
    QUEUE_UNAVAILABLE: 'This could not be started right now. Try again in a moment.',
    RESEARCH_RATE_LIMITED: 'This workspace has read its stores for today. Try again tomorrow.',
    SUPPORT_RATE_LIMITED:
      'You have opened several tickets in the last hour. Reply on an existing one, or try again shortly.',
    SUPPORT_TICKET_CLOSED: 'This ticket is closed. Open a new one to continue.',
    RETRYING: 'Shopify did not respond. Krevya is retrying automatically.',
    RETRIES_EXHAUSTED: 'This could not be finished after several attempts. Try again.',
    PUBLISH_RETRIES_EXHAUSTED:
      'The draft is still available, but Shopify could not publish it. Try publishing again.',
    ROLLBACK_RETRIES_EXHAUSTED:
      'The previous theme could not be restored. Krevya Theme remains live.',
    PRODUCT_NOT_FOUND: 'This product is no longer available in your store.',
    PRODUCT_IMAGE_MISSING: 'This product has no image to work from.',
    MODEL_DISABLED: 'That model is currently unavailable. Pick another one.',
    CREATIVE_SET_DISABLED: 'That creative style is currently unavailable.',
    INVALID_INPUT: 'Some of these settings are not valid. Check and try again.',
    NOT_FOUND: 'We could not find that.',
    INSUFFICIENT_CREDITS: 'Not enough credits for this operation.',
    STORE_LIMIT_REACHED:
      'Your plan has no room for another store. Move to a larger plan on the Billing page to connect it.',
    QUOTE_CHANGED:
      'The price changed while you were configuring. Check the new total and try again.',
    ASSET_STORAGE_FAILED: 'Image storage is unavailable right now. Nothing was charged.',
    CHAT_PROVIDER_NOT_CONFIGURED: 'Magix is not configured for this environment.',
    CHAT_RATE_LIMITED: "You reached today's Magix limit. Try again tomorrow.",
    VIDEO_UNREADABLE: 'We could not read that video file.',
    VIDEO_TOO_SHORT: 'That video is shorter than the model accepts.',
    VIDEO_TOO_LONG: 'That video is longer than the model accepts.',
    MOTION_VIDEO_REQUIRED: 'Motion control needs a motion video.',
    CHARACTER_REQUIRED: 'Motion control needs a character with a frontal photo.',
    SOURCE_VIDEO_REQUIRED: 'Choose a video to edit.',
    MODE_NOT_SUPPORTED: 'This model does not support that generation mode.',
    VIDEO_UPLOAD_IN_USE: 'This video is already attached to a generation job.',
    ADMIN_SESSION_EXPIRED: 'Your admin session ended. Verify again to continue.',
    ADMIN_SESSION_REQUIRED: 'Verify with your authenticator to open the admin area.',
    ADMIN_MFA_ENROLLMENT_REQUIRED: 'Set up an authenticator to use the admin area.',
    STEP_UP_REQUIRED: 'Confirm with your authenticator to continue.',
    MFA_CODE_INVALID: 'Code invalid or expired.',
    ADMIN_OTP_RATE_LIMITED: 'Too many attempts. Wait a moment and try again.',
    REASON_REQUIRED: 'A reason is required.',
    ADMIN_ORIGIN_REJECTED: 'This request was not sent from the app.',
    INTERNAL_ERROR: 'Something went wrong on our side. Try again in a moment.',
  };
  // The page's language first; the English table above is the fallback and
  // the source the Turkish entries were written from.
  return (
    translateNow(`errors.${code}`) ??
    messages[code] ??
    translateNow('errors.INTERNAL_ERROR') ??
    messages['INTERNAL_ERROR']!
  );
}
